import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { doc, getDoc, updateDoc, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { User, UserType } from '../types/alarm';
import { LicensePlan, LicenseInfo, GroupCouponBatch } from '../types/license';
import { licenseService, getRefundAmountKrw, getRefundAmountKrwForActivated, toDateSafe, MONTHLY_PRICE_KRW, REFUND_RATE } from '../services/licenseService';
import { upgradeService } from '../services/upgradeService';
import { displayAsCouponLicense, displayAsGroupLicense, displayAsAdminIssuedLicense } from '../utils/licenseDisplay';
import { getExtensionChainEndDate } from '../utils/licenseExtensionAnchor';
import Header from '../components/Header';
import { Toast } from '../components/Toast';
import NMonthPlanModal, { NMonthModalMode, NMonthOneYearIntent } from '../components/NMonthPlanModal';
import LicenseProofDocumentModal from '../components/LicenseProofDocumentModal';
import { hideScrollbar } from '../styles/hideScrollbar';
import { buildLicenseProofDisplayModel, buildGroupBatchProofDisplayModel } from '../utils/licenseProofDisplay';

// Firestore 데이터를 Date 객체로 변환하는 유틸리티 함수
function coerceToDate(value: any): Date {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') return new Date(value);
  if (typeof value === 'object' && typeof value.toDate === 'function') return value.toDate(); // Firestore Timestamp
  return new Date(value);
}

interface LicensePageProps {
  user: User;
  onLogout: () => void;
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'assignment-table' | 'assignment-card' | 'routine-detail' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => void;
  onUpdateUser: (updatedUser: User) => void;
  onProfileModalOpen: () => void;
  onClassManageModalOpen: () => void;
  classes: any[];
  currentClass: any | null;
  memberGroups?: string[];
  currentMemberGroup?: string;
  onMemberGroupChange?: (group: string) => void;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onSeatAssignmentClick?: () => void;
  onLevelSettingsClick?: () => void;
  onPointManagementClick?: () => void;
  onItemCardSettingsClick?: () => void;
  onAssignmentSettingsClick?: () => void;
}

const LicensePage: React.FC<LicensePageProps> = ({
  user,
  onLogout,
  onNavigate,
  onUpdateUser,
  onProfileModalOpen,
  onClassManageModalOpen,
  classes,
  currentClass,
  memberGroups,
  currentMemberGroup,
  onMemberGroupChange,
  themeMode,
  onToggleTheme,
  onSeatAssignmentClick,
  onLevelSettingsClick,
  onPointManagementClick,
  onItemCardSettingsClick,
  onAssignmentSettingsClick,
}) => {
  const navigate = useNavigate();
  
  const handleTermsClick = () => {
    if (onNavigate) {
      onNavigate('terms-of-service');
    } else {
      navigate('/terms-of-service');
    }
  };
  
  const handlePrivacyClick = () => {
    if (onNavigate) {
      onNavigate('privacy-policy');
    } else {
      navigate('/privacy-policy');
    }
  };
  const [plans, setPlans] = useState<LicensePlan[]>([]);
  const [licenseInfo, setLicenseInfo] = useState<LicenseInfo | null>(null);
  const [latestUserData, setLatestUserData] = useState<User | null>(null);
  const [groupCouponBatches, setGroupCouponBatches] = useState<GroupCouponBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<1 | 12>(1);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [purchaseMode, setPurchaseMode] = useState<'single' | 'group'>('single');
  const [couponCodePrefix, setCouponCodePrefix] = useState(''); // 앞부분 (3자리: B1Y, B6M, S1Y, S6M, P1Y, P6M)
  const [couponCodeSuffix, setCouponCodeSuffix] = useState(''); // 뒷부분 (7자리: 문자3개+숫자4개)
  const [isProcessingCoupon, setIsProcessingCoupon] = useState(false);
  const [pendingGroupCouponCode, setPendingGroupCouponCode] = useState<string | null>(null);
  const [isComposingPrefix, setIsComposingPrefix] = useState(false); // 한글 조합 중인지 추적 (앞부분)
  const [isComposingSuffix, setIsComposingSuffix] = useState(false); // 한글 조합 중인지 추적 (뒷부분)
  const [showTestPasswordModal, setShowTestPasswordModal] = useState(false);
  const [testPassword, setTestPassword] = useState('');
  const [pendingPlan, setPendingPlan] = useState<LicensePlan | null>(null);
  const [isProcessingRefund, setIsProcessingRefund] = useState<{ [paymentId: string]: boolean }>({});
  const [isProcessingGroupRefund, setIsProcessingGroupRefund] = useState<{ [batchId: string]: boolean }>({});
  const [isProcessingActivation, setIsProcessingActivation] = useState<{ [paymentId: string]: boolean }>({});
  const [showAllLicenses, setShowAllLicenses] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastMessageDetail, setToastMessageDetail] = useState('');
  const [nMonthModalPlan, setNMonthModalPlan] = useState<LicensePlan | null>(null);
  const [pendingNMonth, setPendingNMonth] = useState<{ plan: LicensePlan; months: number; quantity: number; isExtension: boolean; yearExtensionFinalAmount?: number } | null>(null);
  const [pendingGroupPurchase, setPendingGroupPurchase] = useState<{ plan: LicensePlan; months: number; quantity: number; groupName: string; targetEmail: string } | null>(null);
  const [isProcessingTestGroup, setIsProcessingTestGroup] = useState(false);
  const [showAllGroupCoupons, setShowAllGroupCoupons] = useState(false);
  const [yearExtensionModalPlan, setYearExtensionModalPlan] = useState<LicensePlan | null>(null); // 1년 탭 동일 플랜 기간 확장
  const [yearUpgradeModalPlan, setYearUpgradeModalPlan] = useState<LicensePlan | null>(null); // 1년 탭 상위 플랜 기간 연장+업그레이드
  const [pendingYearUpgrade, setPendingYearUpgrade] = useState<{ plan: LicensePlan; amount: number } | null>(null); // 테스트 모드 1년 업그레이드
  const [toastConfirmOnly, setToastConfirmOnly] = useState(false); // true면 확인 버튼 클릭 시에만 닫힘
  const [showRefundOrderModal, setShowRefundOrderModal] = useState(false);
  const [refundModalLicense, setRefundModalLicense] = useState<any>(null);
  const [refundModalGroupBatch, setRefundModalGroupBatch] = useState<GroupCouponBatch | null>(null);
  const [licenseProofModal, setLicenseProofModal] = useState<
    | { source: 'license'; license: any }
    | { source: 'group'; batch: GroupCouponBatch }
    | null
  >(null);
  const [groupRefundQuote, setGroupRefundQuote] = useState<{
    refundKrw: number;
    unusedRefundKrw: number;
    activatedRefundKrw: number;
    unusedCount: number;
    activatedRefundedCount: number;
    unitPriceKrw: number;
    quantity: number;
  } | null>(null);
  const [yearExtAgree1, setYearExtAgree1] = useState(false);
  const [yearExtAgree2, setYearExtAgree2] = useState(false);
  const [yearUpgradeAgree1, setYearUpgradeAgree1] = useState(false);
  const [yearUpgradeAgree2, setYearUpgradeAgree2] = useState(false);
  const [oneYearModalIntent, setOneYearModalIntent] = useState<NMonthOneYearIntent | null>(null);

  /** Firestore appSettings/billing 갱신 시 이용권 UI가 다시 읽도록 트리거 */
  const [billingSettingsSync, setBillingSettingsSync] = useState(0);

  useEffect(() => {
    const billingRef = doc(db, 'appSettings', 'billing');
    const unsub = onSnapshot(
      billingRef,
      (snap) => {
        if (!snap.exists()) return;
        const d = snap.data() as { paidPaymentEnabled?: unknown; paymentMode?: unknown };
        if (typeof d.paidPaymentEnabled === 'boolean') {
          localStorage.setItem('paidPaymentEnabled', d.paidPaymentEnabled ? 'true' : 'false');
        }
        if (d.paymentMode === 'test' || d.paymentMode === 'production') {
          localStorage.setItem('paymentMode', d.paymentMode);
        }
        setBillingSettingsSync((n) => n + 1);
      },
      (err) => console.error('청구 설정 구독 오류:', err)
    );
    return unsub;
  }, []);

  // 관리자가 저장한 값(Firestore → 위 구독이 localStorage 반영) + 로컬 기본
  const paidPaymentEnabled =
    billingSettingsSync >= 0 &&
    typeof window !== 'undefined' &&
    localStorage.getItem('paidPaymentEnabled') === 'true';
  // test@edukodi.com 테스트 계정은 잠금 상태에서도 유료 결제(플랜 블록 활성화) 가능
  const effectivePaidPaymentEnabled = paidPaymentEnabled || (user?.email === 'test@edukodi.com');

  useEffect(() => {
    if (yearExtensionModalPlan) {
      setYearExtAgree1(false);
      setYearExtAgree2(false);
    }
  }, [yearExtensionModalPlan]);
  useEffect(() => {
    if (yearUpgradeModalPlan) {
      setYearUpgradeAgree1(false);
      setYearUpgradeAgree2(false);
    }
  }, [yearUpgradeModalPlan]);

  // 차액 계산 유틸리티 함수
  const calculateUpgradeAmount = (
    currentPlanPrice: number,
    targetPlanPrice: number,
    startDate: Date,
    endDate: Date,
    upgradeDate: Date
  ): number => {
    // 남은 일수 계산 (inclusive)
    const daysInclusive = (start: Date, end: Date): number => {
      const startYmd = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endYmd = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      const diffTime = endYmd.getTime() - startYmd.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return diffDays + 1; // inclusive
    };

    const T = daysInclusive(startDate, endDate); // 전체 기간
    const R = daysInclusive(upgradeDate, endDate); // 남은 기간

    // 차액 계산: (targetPrice - currentPrice) * R / T
    const priceDiff = targetPlanPrice - currentPlanPrice;
    const amountKrw = Math.max(0, Math.round((priceDiff * R) / T)); // 반올림
    return amountKrw;
  };

  // 테스트 모드 환불 계산식 메시지 생성 (금액은 licenseService.getRefundAmountKrw와 동일)
  const createRefundMessage = (paidAmount: number): string => {
    const refundAmount = getRefundAmountKrw(paidAmount);
    const feeAmount = paidAmount - refundAmount;
    return `[테스트 모드 환불 계산]\n\n` +
      `결제금액: ${paidAmount.toLocaleString()}원\n` +
      `환불 금액 = 결제금액 × ${REFUND_RATE}\n` +
      `= ${paidAmount.toLocaleString()} × ${REFUND_RATE}\n` +
      `= ${refundAmount.toLocaleString()}원\n\n` +
      `수수료 (${(1 - REFUND_RATE) * 100}%) = ${feeAmount.toLocaleString()}원\n\n` +
      `(계산식: 이용권 구매 또는 업그레이드 후 7일 이내 환불 시 ${REFUND_RATE * 100}% 환불, 운영수수료 공제)`;
  };

  const effectiveUser = latestUserData && latestUserData.id === user.id ? latestUserData : user;

  // 전체 라이센스 배열 가져오기 (필터링 없음)
  const getAllLicensesArray = (): Array<any> => {
    const result: any[] = [];
    
    // 1. licenses 배열이 있으면 먼저 추가
    if (effectiveUser.licenses && Array.isArray(effectiveUser.licenses) && effectiveUser.licenses.length > 0) {
      effectiveUser.licenses.forEach((lic: any) => {
        const purchasedAt = coerceToDate(lic.purchasedAt);
        const expiresAt = coerceToDate(lic.expiresAt);
        const periodStart = lic.periodStart ? coerceToDate(lic.periodStart) : undefined;
        const paidAt = lic.paidAt ? coerceToDate(lic.paidAt) : undefined;
        const activatedAt = lic.activatedAt ? coerceToDate(lic.activatedAt) : undefined;
        const refundedAt = lic.refundedAt ? coerceToDate(lic.refundedAt) : undefined;
        result.push({
          ...lic,
          purchasedAt,
          expiresAt,
          periodStart,
          paidAt,
          activatedAt,
          refundedAt,
          isRefunded: lic.isRefunded === true,
        });
      });
    }
    
    // 2. 하위 호환: license가 있고, licenses 배열에 같은 paymentId가 없으면 추가
    if (effectiveUser.license) {
      const lic = effectiveUser.license;
      const paymentId = lic.paymentId;
      
      // licenses 배열에 같은 paymentId가 있는지 확인
      const alreadyExists = result.some((l: any) => l.paymentId === paymentId);
      
      if (!alreadyExists) {
        result.push({
          ...lic,
          purchasedAt: coerceToDate(lic.purchasedAt),
          expiresAt: coerceToDate(lic.expiresAt),
          periodStart: lic.periodStart ? coerceToDate(lic.periodStart) : undefined,
          paidAt: lic.paidAt ? coerceToDate(lic.paidAt) : undefined,
          activatedAt: lic.activatedAt ? coerceToDate(lic.activatedAt) : undefined,
          purchaseType: 'initial' as const,
          isRefunded: false,
        });
      }
    }

    // 활성 이용권은 활성화일(activatedAt) 기준 최신순, 미활성은 구매일(paidAt ?? purchasedAt) 기준 최신순
    const getSortTime = (l: any) => {
      if (l.activatedAt) return l.activatedAt.getTime();
      const pa = l.paidAt ?? l.purchasedAt;
      return pa ? pa.getTime() : 0;
    };
    return result.sort((a, b) => getSortTime(b) - getSortTime(a));
  };

  // 라이센스 배열 가져오기 (showAllLicenses에 따라 필터링)
  const getLicensesArray = (): Array<any> => {
    const allLicenses = getAllLicensesArray();
    
    // showAllLicenses가 false이면 환불된 라이센스 필터링
    if (!showAllLicenses) {
      return allLicenses.filter((lic: any) => lic.isRefunded !== true);
    }
    
    return allLicenses;
  };

  // 환불 가능 기간 계산 (구매일/결제일 기준 7일 이내)
  const getRefundEligibility = (license: { purchasedAt?: Date; paidAt?: Date; paymentId: string }) => {
    const purchaseOrPaid = (license as any).paidAt ?? license.purchasedAt;
    if (!license || !purchaseOrPaid) {
      return { isEligible: false, daysRemaining: 0, refundEligibleUntil: null, refundEligibleFrom: null };
    }

    const purchasedAt = purchaseOrPaid instanceof Date
      ? purchaseOrPaid
      : new Date(purchaseOrPaid);

    const now = new Date();
    const refundEligibleUntil = new Date(purchasedAt);
    refundEligibleUntil.setDate(refundEligibleUntil.getDate() + 7); // 7일 추가

    const daysRemaining = Math.ceil((refundEligibleUntil.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const isEligible = daysRemaining > 0;

    return { isEligible, daysRemaining, refundEligibleUntil, refundEligibleFrom: purchasedAt };
  };

  const formatYmdToDisplay = (ymd?: string | null) => {
    if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '-';
    return ymd.replace(/-/g, '.');
  };

  const formatDateForDisplay = (date?: Date | string | null) => {
    if (!date) return '-';
    const parsed = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsed.getTime())) return '-';
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}.${month}.${day}`;
  };

  const getGroupActivationValidUntilYmd = (batch: GroupCouponBatch) => {
    if (batch.activationValidUntilYmd) return batch.activationValidUntilYmd;
    const fallback = new Date(batch.createdAt);
    fallback.setMonth(fallback.getMonth() + 1);
    const y = fallback.getFullYear();
    const m = String(fallback.getMonth() + 1).padStart(2, '0');
    const d = String(fallback.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getGroupRefundableCount = (batch: GroupCouponBatch) => {
    const quantity = Number(batch.quantity || 0);
    const activatedCount = Number(batch.activatedCount || 0);
    const refundedCount = Number(batch.refundedCount || 0);
    return Math.max(0, quantity - activatedCount - refundedCount);
  };

  const getGroupRefundEligibility = (batch: GroupCouponBatch) => {
    const today = new Date();
    const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const refundableCount = getGroupRefundableCount(batch);
    const activatedCount = Number(batch.activatedCount || 0);
    const isEligible = !!batch.refundEligibleUntilYmd && todayYmd <= batch.refundEligibleUntilYmd && (refundableCount > 0 || activatedCount > 0);
    return {
      isEligible,
      refundableCount,
      refundEligibleUntilYmd: batch.refundEligibleUntilYmd,
    };
  };

  // 라이선스 만료 여부 확인 (특정 라이센스에 대해)
  const isLicenseExpired = (license: { expiresAt: Date; isRefunded?: boolean }) => {
    if (!license || !license.expiresAt || license.isRefunded === true) {
      return false; // 환불된 라이선스는 만료로 처리하지 않음
    }

    const expiresAt = license.expiresAt instanceof Date
      ? license.expiresAt
      : new Date(license.expiresAt);

    const now = new Date();
    return expiresAt.getTime() < now.getTime();
  };

  // 가장 최근 환불 가능한 라이선스 찾기
  const getLatestRefundableLicense = () => {
    const allLicenses = getAllLicensesArray();
    // 환불되지 않고 환불 가능 기간이 남아있는 라이선스 중 가장 최근 것
    return allLicenses.find((lic: any) => {
      if (lic.isRefunded === true) return false;
      const { isEligible } = getRefundEligibility(lic);
      return isEligible;
    });
  };

  // 미활성화 이용권 전액 환불 (활성화하지 않은 이용권 블록에서 [이용권 환불] 클릭 시)
  const handleRefundUnactivatedLicense = async (license: { paymentId: string; amount?: number }) => {
    const ok = window.confirm('활성화하지 않은 이용권을 환불합니다. 진행하시겠습니까?');
    if (!ok) return;
    setIsProcessingRefund((prev) => ({ ...prev, [license.paymentId]: true }));
    try {
      const paymentMode = localStorage.getItem('paymentMode') || 'test';
      const refundResult =
        paymentMode === 'test'
          ? await licenseService.resetLicenseToFree(user.id, license.paymentId)
          : await licenseService.refundLicensePayment(license.paymentId);
      if (refundResult.success) {
        setToastMessage('전액 환불이 완료되었습니다.\n이용권이 환불 상태로 변경되었습니다.');
        setToastMessageDetail(paymentMode === 'test' ? '계산식: 결제금액 - 취소수수료(10%)' : '카드사에 따라 환불 입금까지 며칠 걸릴 수 있습니다.');
        setToastConfirmOnly(true);
        setShowToast(true);
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          onUpdateUser(userSnap.data() as User);
          Object.assign(user, userSnap.data());
        }
        await loadData();
      } else {
        alert(refundResult.error || '환불 처리에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('미활성 이용권 환불 실패:', error);
      alert(error?.message || '환불 처리에 실패했습니다.');
    } finally {
      setIsProcessingRefund((prev) => ({ ...prev, [license.paymentId]: false }));
    }
  };

  // 환불 실제 실행 (테스트: Firestore 직접 / 실결제: PG 환불 + Functions)
  const performRefundRequest = async (license: { paymentId: string; amount?: number; planType: string; purchaseType?: string; previousPlanType?: string }) => {
    setIsProcessingRefund((prev) => ({ ...prev, [license.paymentId]: true }));
    try {
      const paymentMode = localStorage.getItem('paymentMode') || 'test';
      if (paymentMode === 'test') {
        const refundResult = await licenseService.resetLicenseToFree(user.id, license.paymentId);
        if (!refundResult.success) {
          alert(refundResult.error || '환불 처리에 실패했습니다.');
          return;
        }
      } else {
        const refundResult = await licenseService.refundLicensePayment(license.paymentId);
        if (!refundResult.success) {
          alert(refundResult.error || '환불 처리에 실패했습니다.');
          return;
        }
      }

      setRefundModalLicense(null);
      setToastMessage('환불이 완료되었습니다.\n환불된 이용권이 반영되었습니다.');
      setToastMessageDetail(
        paymentMode === 'test'
          ? '계산식: 결제금액 - 취소수수료(10%) - (일금액×사용일수)'
          : '카드사에 따라 환불 입금까지 며칠 걸릴 수 있습니다.'
      );
      setToastConfirmOnly(true);
      setShowToast(true);
      const userRef = doc(db, 'users', user.id);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const updatedUserData = userSnap.data() as User;
        onUpdateUser(updatedUserData);
        Object.assign(user, updatedUserData);
      }
      await loadData();
      setTimeout(() => loadData(), 100);
    } catch (error: any) {
      console.error('환불 처리 실패:', error);
      setRefundModalLicense(null);
      alert(error?.message || '환불 처리에 실패했습니다.');
    } finally {
      setIsProcessingRefund((prev) => ({ ...prev, [license.paymentId]: false }));
    }
  };

  // 환불 요청 처리 (검증 후 모달 열기 또는 즉시 실행)
  const handleRefundRequest = async (license: { paymentId: string; amount: number; planType: string; purchasedAt?: Date | unknown; isActivated?: boolean; purchaseType?: string; previousPlanType?: string }) => {
    const isActivated = license.isActivated !== false;

    if (isActivated) {
      const { isEligible } = getRefundEligibility(license as any);
      if (!isEligible) {
        alert('환불 가능 기간이 지났습니다. 구매일로부터 7일 이내에만 환불할 수 있습니다.');
        return;
      }
      const { refundKrw } = getRefundAmountKrwForActivated(
        license.amount || 0,
        (license as any).periodStart ?? license.purchasedAt ?? new Date(),
        (license as any).duration ?? 1,
        { isExtension: (license as any).isExtension === true, periodEndDate: (license as any).expiresAt }
      );
      if (refundKrw <= 0) {
        alert('차감액이 환불 금액을 초과하여 환불할 수 없습니다.');
        return;
      }
      setRefundModalLicense(license);
      return;
    }

    const { isEligible } = getRefundEligibility(license as any);
    if (!isEligible) {
      alert('환불 가능 기간이 지났습니다.');
      return;
    }
    const latestRefundableLicense = getLatestRefundableLicense();
    if (latestRefundableLicense && latestRefundableLicense.paymentId !== license.paymentId) {
      setShowRefundOrderModal(true);
      return;
    }

    const paymentMode = localStorage.getItem('paymentMode') || 'test';
    if (paymentMode === 'test') {
      const confirmRefund = window.confirm('활성화하지 않은 이용권을 전액 환불(수수료 10% 차감)합니다. 진행하시겠습니까?');
      if (confirmRefund) await performRefundRequest(license);
      return;
    }
    const confirmRefund = window.confirm(
      '활성화하지 않은 이용권을 환불합니다. 결제 금액의 90%가 카드로 환불되며, 이용권은 즉시 해지됩니다. 진행하시겠습니까?'
    );
    if (confirmRefund) await performRefundRequest(license);
  };

  // 이용권 활성화 처리 (등록된 이용권을 활성화)
  const handleActivateLicense = async (license: { paymentId: string; planType: string; duration: number }) => {
    if (!license?.paymentId) return;
    if (isProcessingActivation[license.paymentId]) return;

    setIsProcessingActivation(prev => ({ ...prev, [license.paymentId]: true }));
    try {
      const result = await licenseService.activateLicense(license.paymentId);
      if (result.success) {
        setToastMessage('이용권이 활성화되었습니다.');
        setToastMessageDetail('');
        setToastConfirmOnly(true);
        setShowToast(true);
        // Firestore에서 최신 user(licenses 포함)를 가져와 즉시 UI 반영
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const latestUser = userSnap.data() as User;
          onUpdateUser(latestUser);
        }
        await loadData();
      } else {
        alert(result.error || '이용권 활성화에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('이용권 활성화 실패:', error);
      alert(error.message || '이용권 활성화에 실패했습니다.');
    } finally {
      setIsProcessingActivation(prev => ({ ...prev, [license.paymentId]: false }));
    }
  };

  const handleGroupCouponRefundRequest = async (batch: GroupCouponBatch) => {
    const eligibility = getGroupRefundEligibility(batch);
    if (!eligibility.isEligible) {
      alert('환불 가능 기간이 지났거나 환불 가능한 그룹 이용권이 없습니다.');
      return;
    }
    if (isProcessingGroupRefund[batch.batchId]) return;
    try {
      const quote = await upgradeService.getGroupCouponRefundQuote(batch.batchId);
      if (!quote.success || quote.refundKrw == null) {
        alert(quote.error || '환불 금액을 조회할 수 없습니다.');
        return;
      }
      setGroupRefundQuote({
        refundKrw: quote.refundKrw,
        unusedRefundKrw: quote.unusedRefundKrw ?? 0,
        activatedRefundKrw: quote.activatedRefundKrw ?? 0,
        unusedCount: quote.unusedCount ?? 0,
        activatedRefundedCount: quote.activatedRefundedCount ?? 0,
        unitPriceKrw: quote.unitPriceKrw ?? 0,
        quantity: quote.quantity ?? 0,
      });
      setRefundModalGroupBatch(batch);
    } catch (error: any) {
      console.error('그룹 이용권 환불 견적 조회 실패:', error);
      alert(error?.message || '환불 금액 조회에 실패했습니다.');
    }
  };

  const performGroupCouponRefund = async () => {
    if (!refundModalGroupBatch) return;
    const batchId = refundModalGroupBatch.batchId;
    if (isProcessingGroupRefund[batchId]) return;
    setIsProcessingGroupRefund((prev) => ({ ...prev, [batchId]: true }));
    try {
      const result = await upgradeService.refundOwnGroupCouponBatch(batchId);
      if (!result.success) {
        alert(result.error || '그룹 이용권 환불에 실패했습니다.');
        return;
      }
      const unused = Number(result.unusedRefundKrw || 0);
      const activated = Number(result.activatedRefundKrw || 0);
      const total = Number(result.refundKrw || 0);
      setToastMessage(`그룹 이용권 환불이 완료되었습니다.\n환불 금액: ${total.toLocaleString()}원`);
      setToastMessageDetail(`미사용 ${unused.toLocaleString()}원 + 활성화 부분환불 ${activated.toLocaleString()}원 = ${total.toLocaleString()}원 (수수료 10%·사용일수 차감)`);
      setToastConfirmOnly(true);
      setShowToast(true);
      setRefundModalGroupBatch(null);
      setGroupRefundQuote(null);
      const refreshed = await loadData();
      if (refreshed) onUpdateUser(refreshed);
    } catch (error: any) {
      console.error('그룹 이용권 환불 실패:', error);
      alert(error?.message || '그룹 이용권 환불에 실패했습니다.');
    } finally {
      setIsProcessingGroupRefund((prev) => ({ ...prev, [batchId]: false }));
    }
  };

  // 플랜의 features를 카테고리별로 그룹화하는 함수 (플랜 비교표 순서대로 정렬)
  const groupFeaturesByCategory = (planType: string): Array<{ category: string; features: string[] }> => {
    const categories: Array<{ category: string; features: string[] }> = [];
    
    // 클래스 기본 카테고리 (고정된 순서: 8개 항목)
    const classBasicFeatures: string[] = [];
    // 1. 클래스 생성 (항상 있음)
    if (planType === 'free' || planType === 'basic') {
      classBasicFeatures.push('클래스 1개');
    } else if (planType === 'standard') {
      classBasicFeatures.push('클래스 2개');
    } else if (planType === 'pro') {
      classBasicFeatures.push('클래스 5개');
    }
    // 2. 멤버 그룹 생성 수 (항상 있음)
    if (planType === 'free' || planType === 'basic') {
      classBasicFeatures.push('기본 1개 그룹');
    } else if (planType === 'standard') {
      classBasicFeatures.push('최대 2개 그룹');
    } else if (planType === 'pro') {
      classBasicFeatures.push('최대 15개 그룹');
    }
    // 3. 멤버 수 (항상 있음, 플랜별 상한)
    if (planType === 'standard' || planType === 'pro') {
      classBasicFeatures.push('멤버 수 35명 (멤버 그룹별)');
    } else {
      classBasicFeatures.push('멤버 수 30명 (멤버 그룹별)');
    }
    // 4. 추가 프로필 세트 사용 (프리 제외)
    if (planType !== 'free') {
      classBasicFeatures.push('추가 프로필 세트 사용');
    } else {
      classBasicFeatures.push(''); // 빈칸
    }
    // 5. 아이템 카드 사용 (프리/베이직 제외)
    if (planType === 'standard' || planType === 'pro') {
      classBasicFeatures.push('아이템 카드 사용');
    } else {
      classBasicFeatures.push(''); // 빈칸
    }
    // 6. 멤버 개별 페이지 접근 (스탠다드/프로만, 베이직 불가)
    if (planType === 'standard' || planType === 'pro') {
      classBasicFeatures.push('멤버 개별 페이지 접근');
    } else {
      classBasicFeatures.push(''); // 빈칸
    }
    // 7. 스토리지 데이터 용량 (스탠다드·프로)
    if (planType === 'standard' || planType === 'pro') {
      classBasicFeatures.push('스토리지 데이터 용량 100MB');
    } else {
      classBasicFeatures.push('');
    }
    // 8. 클래스 설정 연동 (프로만)
    if (planType === 'pro') {
      classBasicFeatures.push('클래스 설정 연동');
    } else {
      classBasicFeatures.push(''); // 빈칸
    }

    categories.push({ category: '클래스 기본', features: classBasicFeatures });
    
    // 알람 카테고리 (고정된 순서: 2개 항목)
    const alarmFeatures: string[] = [];
    // 1. 알람 세트 (플랜별 제한)
    if (planType === 'free') {
      alarmFeatures.push('알람 세트 1개');
    } else if (planType === 'basic') {
      alarmFeatures.push('알람 세트 3개');
    } else if (planType === 'standard') {
      alarmFeatures.push('알람 세트 5개');
    } else {
      alarmFeatures.push('알람 세트 10개');
    }
    // 2. 알람 종류 (항상 있음)
    if (planType === 'free') {
      alarmFeatures.push('기본 알람만');
    } else {
      alarmFeatures.push('모든 알람 종류');
    }
    categories.push({ category: '알람', features: alarmFeatures });
    
    // 과제 관리 카테고리 (고정된 순서: 3개 항목)
    const assignmentFeatures: string[] = [];
    // 1. 과제 생성 수 (항상 있음)
    if (planType === 'free') {
      assignmentFeatures.push('과제 10개/월');
    } else {
      assignmentFeatures.push('과제 무제한');
    }
    // 2. 이용가능 과제 타입 (항상 있음)
    if (planType === 'free') {
      assignmentFeatures.push('일반 과제만');
    } else if (planType === 'basic') {
      assignmentFeatures.push('일반, 루틴 과제');
    } else {
      assignmentFeatures.push('일반, 루틴, 프로젝트 과제');
    }
    // 3. 멤버 개별 과제 제출·확인 (스탠다드·프로)
    if (planType === 'standard' || planType === 'pro') {
      assignmentFeatures.push('개별 과제(이미지, 링크, 메세지) 제출 및 확인');
    } else {
      assignmentFeatures.push('');
    }
    categories.push({ category: '과제 관리', features: assignmentFeatures });
    
    // 클래스 챗 카테고리 (고정된 순서: 3개 항목)
    const chatFeatures: string[] = [];
    // 1. 클래스챗 사용여부 (항상 있음)
    if (planType === 'free' || planType === 'basic') {
      chatFeatures.push('클래스챗 사용 불가');
    } else {
      chatFeatures.push('클래스챗 사용 가능');
    }
    // 2. 문제 세트 수 (프리/베이직 제외)
    if (planType === 'free' || planType === 'basic') {
      chatFeatures.push(''); // 빈칸
    } else if (planType === 'standard') {
      chatFeatures.push('문제 세트 10개');
    } else if (planType === 'pro') {
      chatFeatures.push('문제 세트 20개');
    }
    // 3. 문제 카드 수 (프리/베이직 제외)
    if (planType === 'free' || planType === 'basic') {
      chatFeatures.push(''); // 빈칸
    } else {
      chatFeatures.push('문제 카드 30개 (세트당)');
    }
    categories.push({ category: '클래스 챗', features: chatFeatures });
    
    // 보드 카테고리 (고정된 순서: 2개 항목)
    const boardFeatures: string[] = [];
    // 1. 전체공지/개별공지 저장 (스탠다드 이상만)
    if (planType === 'standard' || planType === 'pro') {
      boardFeatures.push('전체공지/개별공지 저장');
    } else {
      boardFeatures.push(''); // 빈칸
    }
    // 2. 노트 카테고리 수 (항상 있음)
    if (planType === 'free') {
      boardFeatures.push('노트 카테고리 1개');
    } else if (planType === 'basic') {
      boardFeatures.push('노트 카테고리 2개');
    } else if (planType === 'standard') {
      boardFeatures.push('노트 카테고리 5개');
    } else if (planType === 'pro') {
      boardFeatures.push('노트 카테고리 5개');
    }
    categories.push({ category: '보드', features: boardFeatures });
    
    return categories;
  };

  const loadData = useCallback(async (): Promise<User | null> => {
    try {
      setLoading(true);
      // Firestore에서 최신 user 조회 (테스트 모드·활성화 직후 반영)
      const userRef = doc(db, 'users', user.id);
      const userSnap = await getDoc(userRef);
      // 문서 필드에 id가 없으면 effectiveUser 가드가 실패해 props user(구버전)만 쓰이므로 문서 id를 항상 병합
      const latestUser: User = userSnap.exists()
        ? ({ ...userSnap.data(), id: userSnap.id } as User)
        : user;
      setLatestUserData(latestUser);
      const [plansData, info, groupBatchResult] = await Promise.all([
        licenseService.getPlans(),
        licenseService.getLicenseInfoForUser(latestUser),
        upgradeService.getMyGroupCouponBatches(user.id),
      ]);
      setPlans(plansData);
      setLicenseInfo(info);
      setGroupCouponBatches(groupBatchResult.success && groupBatchResult.batches ? groupBatchResult.batches : []);
      return latestUser;
    } catch (err: any) {
      console.error('이용권 정보 로드 실패:', err);
      setError('이용권 정보를 불러오는데 실패했습니다.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setLatestUserData(null);
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePurchase = async (plan: LicensePlan) => {
    // 프리 플랜은 무료이므로 바로 활성화
    if (plan.planType === 'free') {
        alert('프리 플랜은 이미 활성화되어 있습니다.');
        return;
    }
    // 1년 탭 동일 플랜은 모달에서 처리하므로 여기서는 호출되지 않음
    // 다운그레이드 불가
    if (isDowngrade(plan)) {
        alert('다운그레이드는 불가능합니다. 더 높은 등급의 플랜 또는 같은 기간 이상의 플랜만 선택할 수 있습니다.');
        return;
    }
    // 로컬스토리지에서 PG 결제 모드 확인
    const paymentMode = localStorage.getItem('paymentMode') || 'test';
    if (paymentMode === 'test') {
      setPendingPlan(plan);
      setPendingNMonth(null);
      setShowTestPasswordModal(true);
    } else {
      // 실제 결제 모드: PG 결제 진행
      try {
        // 결제 요청
        const paymentResult = await licenseService.requestPayment(plan, user.id, {
          fullName: user.name,
          email: user.email,
        });

        // 결제 검증 및 이용권 등록 (활성화는 구매 이력에서 별도 진행)
        await licenseService.verifyAndIssueLicense(paymentResult.imp_uid, plan.id);

        setToastMessage(`${plan.name} 이용권이 결제·활성화되었습니다.`);
          setToastMessageDetail('');
          setToastConfirmOnly(true);
          setShowToast(true);
          await loadData();
      } catch (error: any) {
        console.error('결제 처리 실패:', error);
        alert(error.message || '결제 처리에 실패했습니다.');
      }
    }
  };

  const handleTestPasswordConfirm = async () => {
    // 테스트 비밀번호 확인 (환경변수 또는 하드코딩)
    const TEST_PASSWORD = process.env.REACT_APP_TEST_PASSWORD || 'admin1234';
    
    if (testPassword !== TEST_PASSWORD) {
      alert('테스트 비밀번호가 올바르지 않습니다.');
      return;
    }

    if (pendingGroupPurchase) {
      if (isProcessingTestGroup) return; // 이중 호출 방지
      const { plan, months, quantity, groupName, targetEmail } = pendingGroupPurchase;
      setIsProcessingTestGroup(true);
      try {
        const issued = await licenseService.issueGroupCouponsTestMode(plan.id, {
          durationMonths: months,
          quantity,
          groupName,
          targetEmail,
        });
        if (!issued.success) {
          throw new Error(issued.error || '테스트 그룹 이용권 발급에 실패했습니다.');
        }
        setShowTestPasswordModal(false);
        setTestPassword('');
        setPendingGroupPurchase(null);
        setPendingNMonth(null);
        setPendingPlan(null);
        setToastMessage(
          `테스트 모드에서 그룹 코드 발급이 완료되었습니다.\n안내 메일이 ${targetEmail}로 전송됩니다.`
        );
        setToastMessageDetail('');
        setToastConfirmOnly(true);
        setShowToast(true);
        await loadData();
      } catch (error: any) {
        console.error('테스트 모드 그룹 이용권 발급 실패:', error);
        alert(error?.message || '테스트 그룹 이용권 발급에 실패했습니다.');
      } finally {
        setIsProcessingTestGroup(false);
      }
      return;
    }

    if (pendingNMonth) {
      const { plan, months, quantity, isExtension } = pendingNMonth;
      const totalMonths = months * quantity;
      const yearExtAmount = (pendingNMonth as { yearExtensionFinalAmount?: number }).yearExtensionFinalAmount;
      const paymentAmount = (isExtension && plan.duration === 12 && totalMonths === 12)
        ? (yearExtAmount ?? plan.price)
        : plan.price * totalMonths;
      const testPaymentId = `TEST_${Date.now()}_${user.id}`;
      const userRef = doc(db, 'users', user.id);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        alert('사용자 정보를 찾을 수 없습니다.');
        return;
      }
      const userData = userSnap.data() as User & { licenses?: any[] };
      const existingLicenses = Array.isArray(userData?.licenses) ? [...userData.licenses] : [];
      if (userData?.license && !existingLicenses.some((l: any) => l.paymentId === userData.license?.paymentId)) {
        const lic = userData.license as any;
        existingLicenses.push({ ...lic, purchasedAt: lic.purchasedAt, expiresAt: lic.expiresAt, purchaseType: lic.purchaseType || 'initial' });
      }
      if (existingLicenses.some((l: any) => l.paymentId === testPaymentId)) {
        alert('이미 처리된 테스트 결제입니다.');
        return;
      }
      const nowForLicense = new Date();
      const startToday = new Date(nowForLicense.getFullYear(), nowForLicense.getMonth(), nowForLicense.getDate());
      let periodStart: Date;
      let periodEnd: Date;
      if (isExtension && totalMonths === 12) {
        periodStart = new Date(startToday);
        periodEnd = new Date(startToday);
        periodEnd.setFullYear(periodEnd.getFullYear() + 1);
      } else if (isExtension) {
        const currentEnd = getExtensionChainEndDate(userData, plan.planType, testPaymentId);
        if (!currentEnd) {
          periodStart = new Date(startToday);
          periodEnd = new Date(startToday);
          periodEnd.setMonth(periodEnd.getMonth() + totalMonths);
        } else {
          periodStart = new Date(currentEnd);
          periodStart.setDate(periodStart.getDate() + 1);
          periodEnd = new Date(periodStart);
          periodEnd.setMonth(periodEnd.getMonth() + totalMonths);
        }
      } else {
        periodStart = new Date(startToday);
        periodEnd = new Date(startToday);
        if (totalMonths === 12) periodEnd.setFullYear(periodEnd.getFullYear() + 1);
        else periodEnd.setMonth(periodEnd.getMonth() + totalMonths);
      }
      const activatedLicense = {
        planId: plan.id,
        planType: plan.planType,
        duration: totalMonths,
        purchasedAt: Timestamp.fromDate(nowForLicense),
        paidAt: Timestamp.fromDate(nowForLicense),
        periodStart: Timestamp.fromDate(periodStart),
        expiresAt: Timestamp.fromDate(periodEnd),
        activatedAt: Timestamp.fromDate(nowForLicense),
        isActive: true,
        isActivated: true,
        paymentId: testPaymentId,
        amount: paymentAmount,
        currency: 'KRW',
        purchaseType: 'initial' as const,
        isExtension: isExtension ?? false,
      };
      const updatedLicenses = existingLicenses.map((l: any) => ({ ...l, isActive: false }));
      updatedLicenses.push(activatedLicense);
      await updateDoc(userRef, { licenses: updatedLicenses, license: activatedLicense, userType: plan.planType, updatedAt: Timestamp.now() });
      setToastMessage(
        isExtension && totalMonths === 12
          ? `${plan.name} 1년 기간 확장 이용권이 결제·활성화되었습니다.`
          : `${plan.name} ${totalMonths}개월 이용권이 결제·활성화되었습니다.`
      );
      setToastMessageDetail('');
      setToastConfirmOnly(true);
      setShowToast(true);
      setShowTestPasswordModal(false);
      setTestPassword('');
      setPendingNMonth(null);
      setPendingPlan(null);
      await loadData();
      return;
    }

    if (pendingYearUpgrade) {
      const { plan, amount } = pendingYearUpgrade;
      const testPaymentId = `TEST_${Date.now()}_${user.id}`;
      const userRef = doc(db, 'users', user.id);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        alert('사용자 정보를 찾을 수 없습니다.');
        return;
      }
      const userData = userSnap.data() as User & { licenses?: any[] };
      const existingLicenses = Array.isArray(userData?.licenses) ? [...userData.licenses] : [];
      if (existingLicenses.some((l: any) => l.paymentId === testPaymentId)) {
        alert('이미 처리된 테스트 결제입니다.');
        return;
      }
      const now = new Date();
      const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOneYear = new Date(startToday);
      endOneYear.setFullYear(endOneYear.getFullYear() + 1);
      const upgradeLicense = {
        planId: plan.id,
        planType: plan.planType,
        duration: 12,
        purchasedAt: Timestamp.fromDate(now),
        paidAt: Timestamp.fromDate(now),
        periodStart: Timestamp.fromDate(startToday),
        expiresAt: Timestamp.fromDate(endOneYear),
        activatedAt: Timestamp.fromDate(now),
        isActive: true,
        isActivated: true,
        paymentId: testPaymentId,
        amount,
        currency: 'KRW',
        purchaseType: 'upgrade' as const,
      };
      const updatedLicenses = existingLicenses.map((l: any) => ({ ...l, isActive: false }));
      updatedLicenses.push(upgradeLicense);
      await updateDoc(userRef, {
        licenses: updatedLicenses,
        license: upgradeLicense,
        userType: plan.planType,
        updatedAt: Timestamp.now(),
      });
      setToastMessage(`${plan.name} 1년 업그레이드 이용권이 적용되었습니다.`);
      setToastMessageDetail('');
      setToastConfirmOnly(true);
      setShowToast(true);
      setShowTestPasswordModal(false);
      setTestPassword('');
      setPendingYearUpgrade(null);
      setPendingPlan(null);
      await loadData();
      return;
    }

    if (!pendingPlan) {
      alert('플랜 정보를 찾을 수 없습니다.');
      return;
    }

    try {
      // 업그레이드 여부 확인 및 차액 계산
      const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
      const currentLicense = user.license;
      const currentPlanType = user.userType;
      const isPlanUpgrade =
        !!currentPlanType && planOrder.indexOf(pendingPlan.planType) > planOrder.indexOf(currentPlanType);
      const isDurationUpgrade =
        !!currentLicense &&
        currentLicense.planType === pendingPlan.planType &&
        typeof currentLicense.duration === 'number' &&
        pendingPlan.duration > currentLicense.duration;
      const isUpgrade = isPlanUpgrade || isDurationUpgrade;
      
      let paymentAmount = pendingPlan.price;
      
      // 1년 이용권 구매는 N개월 활성 여부와 관계없이 항상 이용권만 등록(미활성). 자동 업그레이드/활성화 하지 않음.
      const isOneYearPlan = pendingPlan.duration === 12;
      const treatAsUpgrade = isUpgrade && currentLicense && !isOneYearPlan;

      if (treatAsUpgrade) {
        // N개월→N개월 업그레이드만: 현재 활성화 플랜 이용기간 기준 차액 계산 후 즉시 활성화
        const periodEnd = getActivePeriodEndDate();
        if (!periodEnd) {
          alert('활성화된 이용권 기간을 확인할 수 없습니다.');
          return;
        }
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endDay = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const remainingDays = Math.max(0, Math.floor((endDay.getTime() - tomorrow.getTime()) / (24 * 60 * 60 * 1000)) + 1);

        const currentMonthly = MONTHLY_PRICE_KRW[currentLicense.planType] ?? 3600;
        const newMonthly = MONTHLY_PRICE_KRW[pendingPlan.planType] ?? 3600;
        const currentDaily = currentMonthly / 30;
        const newDaily = newMonthly / 30;
        const diffDaily = Math.max(0, newDaily - currentDaily);
        paymentAmount = Math.max(0, Math.round(diffDaily * remainingDays));

        } else {
        // 초기 구매 또는 1년 이용권 구매: 정가 그대로, 이용권만 등록(미활성)
      }

      // 테스트 모드: PG 결제 없이 이용권만 등록 (실제 결제와 동일하게 활성화는 구매 이력에서 진행)
      const testPaymentId = `TEST_${Date.now()}_${user.id}`;
      const now = new Date();
      const userRef = doc(db, 'users', user.id);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        alert('사용자 정보를 찾을 수 없습니다.');
        return;
      }
      const userData = userSnap.data() as User & { licenses?: any[] };
      const existingLicenses = Array.isArray(userData?.licenses) ? [...userData.licenses] : [];
      if (userData?.license && !existingLicenses.some((l: any) => l.paymentId === userData.license?.paymentId)) {
        const lic = userData.license as any;
        existingLicenses.push({
          ...lic,
          purchasedAt: lic.purchasedAt,
          expiresAt: lic.expiresAt,
          purchaseType: lic.purchaseType || 'initial',
        });
      }
      if (existingLicenses.some((l: any) => l.paymentId === testPaymentId)) {
        alert('이미 처리된 테스트 결제입니다.');
        return;
      }
      if (treatAsUpgrade) {
        // N개월→N개월 업그레이드만: 오늘~현재 활성화 플랜 만료일까지 상위 플랜 이용권 새로 생성 후 즉시 활성화
        const periodEnd = getActivePeriodEndDate();
        if (!periodEnd) {
          alert('활성화된 이용권 기간을 확인할 수 없습니다.');
          return;
        }
        const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());

        const upgradeLicense = {
          planId: pendingPlan.id,
          planType: pendingPlan.planType,
          duration: pendingPlan.duration,
          purchasedAt: Timestamp.fromDate(now),
          paidAt: Timestamp.fromDate(now),
          periodStart: Timestamp.fromDate(startToday),
          expiresAt: Timestamp.fromDate(periodEnd),
          activatedAt: Timestamp.fromDate(now),
          isActive: true,
          isActivated: true,
          paymentId: testPaymentId,
          amount: paymentAmount,
          currency: 'KRW',
          purchaseType: 'upgrade' as const,
          previousPlanType: currentLicense.planType,
          previousPaymentId: currentLicense.paymentId,
        };

        const updatedLicenses = existingLicenses.map((l: any) => ({ ...l, isActive: false }));
        updatedLicenses.push(upgradeLicense);

        await updateDoc(userRef, {
          licenses: updatedLicenses,
          license: upgradeLicense,
          userType: pendingPlan.planType,
          updatedAt: Timestamp.now(),
        });

        setToastMessage(`${pendingPlan.name} 업그레이드 이용권이 적용되었습니다. 오늘부터 만료일까지 상위 플랜으로 이용됩니다.`);
          setToastMessageDetail('');
        } else {
        const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const periodEnd = new Date(startToday);
        if (pendingPlan.duration === 12) periodEnd.setFullYear(periodEnd.getFullYear() + 1);
        else periodEnd.setMonth(periodEnd.getMonth() + pendingPlan.duration);
        const activatedLicense = {
          planId: pendingPlan.id,
          planType: pendingPlan.planType,
          duration: pendingPlan.duration,
          purchasedAt: Timestamp.fromDate(now),
          paidAt: Timestamp.fromDate(now),
          periodStart: Timestamp.fromDate(startToday),
          expiresAt: Timestamp.fromDate(periodEnd),
          activatedAt: Timestamp.fromDate(now),
          isActive: true,
          isActivated: true,
          paymentId: testPaymentId,
          amount: paymentAmount,
          currency: 'KRW',
          purchaseType: 'initial' as const,
        };
        const updatedLicenses = existingLicenses.map((l: any) => ({ ...l, isActive: false }));
        updatedLicenses.push(activatedLicense);
        await updateDoc(userRef, {
          licenses: updatedLicenses,
          license: activatedLicense,
          userType: pendingPlan.planType,
          updatedAt: Timestamp.now(),
        });

        setToastMessage(`${pendingPlan.name} 이용권이 결제·활성화되었습니다.`);
        setToastMessageDetail('');
      }
        setToastConfirmOnly(true);
        setShowToast(true);
        setShowTestPasswordModal(false);
        setTestPassword('');
        setPendingPlan(null);
        await loadData();
    } catch (error: any) {
      console.error('테스트 모드 이용권 활성화 실패:', error);
      alert(error.message || '이용권 활성화에 실패했습니다.');
    }
  };

  const handleTestPasswordCancel = () => {
    setShowTestPasswordModal(false);
    setTestPassword('');
    setPendingPlan(null);
    setPendingNMonth(null);
    setPendingGroupPurchase(null);
    setPendingYearUpgrade(null);
  };

  const handleNMonthConfirmAdd = (plan: LicensePlan, months: number) => {
    const quantity = 1;
    const currentActive = getCurrentActiveLicense();
    const isExtension = !!(currentActive.planType && currentActive.planType !== 'free' && currentActive.planType === plan.planType);
    setNMonthModalPlan(null);
    const paymentMode = localStorage.getItem('paymentMode') || 'test';
    if (paymentMode === 'test') {
      setPendingNMonth({ plan, months, quantity, isExtension });
      setPendingPlan(null);
      setShowTestPasswordModal(true);
    } else {
      const totalAmount = plan.price * months * quantity;
      licenseService.requestPayment(plan, user.id, { fullName: user.name, email: user.email }, { totalAmount, durationMonths: months, quantity, isExtension })
        .then((res) => licenseService.verifyAndIssueLicense(res.imp_uid, plan.id, { durationMonths: months, quantity, isExtension }))
        .then(() => {
          setToastMessage(`${plan.name} ${months}개월 이용권이 결제·활성화되었습니다.`);
          setToastMessageDetail('');
          setToastConfirmOnly(true);
          setShowToast(true);
          loadData();
        })
        .catch((err: any) => {
          alert(err?.message || '결제 처리에 실패했습니다.');
        });
    }
  };

  const handleGroupPurchaseConfirm = async (
    plan: LicensePlan,
    payload: { months: number; quantity: number; groupName: string; targetEmail: string }
  ) => {
    const months = plan.duration === 12 ? 12 : payload.months;
    const quantity = payload.quantity;
    setNMonthModalPlan(null);
    setOneYearModalIntent(null);

    const paymentMode = localStorage.getItem('paymentMode') || 'test';
    if (paymentMode === 'test') {
      setPendingGroupPurchase({
        plan,
        months,
        quantity,
        groupName: payload.groupName,
        targetEmail: payload.targetEmail,
      });
      setPendingPlan(null);
      setPendingNMonth(null);
      setShowTestPasswordModal(true);
      return;
    }

    const totalAmount = plan.duration === 12
      ? plan.price * quantity
      : plan.price * months * quantity;
    try {
      const paymentResult = await licenseService.requestPayment(
        plan,
        user.id,
        { fullName: user.name, email: user.email },
        {
          totalAmount,
          durationMonths: months,
          quantity,
          groupPurchase: {
            groupName: payload.groupName,
            targetEmail: payload.targetEmail,
            quantity,
          },
        }
      );
      const issued = await licenseService.verifyAndIssueGroupCoupons(paymentResult.imp_uid, plan.id, {
        durationMonths: months,
        quantity,
        groupName: payload.groupName,
        targetEmail: payload.targetEmail,
      });
      if (!issued.success) throw new Error(issued.error || '그룹 이용권 발급에 실패했습니다.');
      setToastMessage(`그룹 코드 발급이 완료되었습니다.\n안내 메일이 ${payload.targetEmail}로 전송됩니다.`);
      setToastMessageDetail('');
      setToastConfirmOnly(true);
      setShowToast(true);
      await loadData();
    } catch (err: any) {
      alert(err?.message || '그룹 이용권 결제 처리에 실패했습니다.');
    }
  };

  const handleNMonthConfirmUpgrade = (plan: LicensePlan) => {
    setNMonthModalPlan(null);
    handlePurchase(plan);
  };

  // 1년 기간 확장: 현재 시점부터 1년 이용기간 재설정, 확장된 날 수 × (할인가/365) 차액만 결제
  const handleYearExtensionConfirm = (planArg?: LicensePlan) => {
    const plan = planArg ?? yearExtensionModalPlan;
    if (!plan || plan.duration !== 12) return;
    const ext = calculateOneYearExtensionPrice(plan);
    if (!ext || ext.isRestricted) return;
    const totalAmount = ext.finalAmount;
    setYearExtensionModalPlan(null);
    const paymentMode = localStorage.getItem('paymentMode') || 'test';
    if (paymentMode === 'test') {
      setPendingPlan(null);
      setPendingNMonth({ plan, months: 12, quantity: 1, isExtension: true, yearExtensionFinalAmount: totalAmount });
      setShowTestPasswordModal(true);
    } else {
      licenseService.requestPayment(plan, user.id, { fullName: user.name, email: user.email }, { totalAmount, durationMonths: 12, quantity: 1, isExtension: true })
        .then((res) => licenseService.verifyAndIssueLicense(res.imp_uid, plan.id, { durationMonths: 12, quantity: 1, isExtension: true }))
        .then(() => {
          setToastMessage(`${plan.name} 1년 기간 확장 이용권이 결제·활성화되었습니다.`);
          setToastMessageDetail('');
          setToastConfirmOnly(true);
          setShowToast(true);
          loadData();
        })
        .catch((err: any) => {
          alert(err?.message || '결제 처리에 실패했습니다.');
        });
    }
  };

  // 1년 업그레이드: (1년 상위 가격 - 남은 기간 현재 플랜 가치) 결제 → 현재부터 1년 상위 플랜 이용권 발급
  const handleYearUpgradeConfirm = (planArg?: LicensePlan) => {
    const plan = planArg ?? yearUpgradeModalPlan;
    if (!plan) return;
    const result = calculateOneYearUpgradePrice(plan);
    if (!result || result.price <= 0) return;
    setYearUpgradeModalPlan(null);
    const paymentMode = localStorage.getItem('paymentMode') || 'test';
    if (paymentMode === 'test') {
      setPendingYearUpgrade({ plan, amount: result.price });
      setPendingPlan(null);
      setShowTestPasswordModal(true);
    } else {
      licenseService.requestPayment(plan, user.id, { fullName: user.name, email: user.email }, { totalAmount: result.price })
        .then((res) => licenseService.verifyAndIssueLicense(res.imp_uid, plan.id, { oneYearUpgrade: true }))
        .then(() => {
          setToastMessage(`${plan.name} 1년 업그레이드 이용권이 적용되었습니다.`);
          setToastMessageDetail('');
          setToastConfirmOnly(true);
          setShowToast(true);
          loadData();
        })
        .catch((err: any) => {
          alert(err?.message || '결제 처리에 실패했습니다.');
        });
    }
  };

  const hasCurrentActiveLicense = () => {
    const lic: any = effectiveUser?.license;
    if (!lic) return false;
    if (lic.isActive === false || lic.isRefunded === true) return false;
    const expiresAt = coerceToDate(lic.expiresAt);
    if (!expiresAt) return false;
    return expiresAt.getTime() > Date.now();
  };

  const submitCouponCode = async (fullCode: string, forceApplyExistingLicense: boolean) => {
    setIsProcessingCoupon(true);
    try {
      const result = await licenseService.activateLicenseWithCoupon(user.id, fullCode, { forceApplyExistingLicense });

      if (result.success && result.license) {
        const updatedUser: User = {
          ...user,
          userType: result.license.planType,
          license: result.license,
        };
        onUpdateUser(updatedUser);

        alert('쿠폰이 성공적으로 등록되었습니다. 1년 이용권이 활성화되었습니다.');
        setCouponCodePrefix('');
        setCouponCodeSuffix('');
        setPendingGroupCouponCode(null);
        setShowCouponModal(false);
        await loadData();
      } else if (result.requireExistingLicenseConfirmation) {
        setPendingGroupCouponCode(fullCode);
        setShowCouponModal(false);
      } else {
        alert(result.error || '쿠폰 등록에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('쿠폰 등록 실패:', error);
      alert(error.message || '쿠폰 등록에 실패했습니다.');
    } finally {
      setIsProcessingCoupon(false);
    }
  };

  const handleCouponSubmit = async () => {
    const prefixUpper = couponCodePrefix.toUpperCase();
    const suffixUpper = couponCodeSuffix.toUpperCase();
    const fullCode = `${prefixUpper}-${suffixUpper}`.trim();

    if (!couponCodePrefix.trim() || !couponCodeSuffix.trim()) {
      alert('쿠폰 코드를 모두 입력해주세요.');
      return;
    }

    if (couponCodePrefix.length !== 3) {
      alert('쿠폰 코드 앞부분은 3자리여야 합니다.');
      return;
    }

    const requiredSuffixLen = prefixUpper === 'GRP' ? 9 : 7;
    if (couponCodeSuffix.length !== requiredSuffixLen) {
      alert(prefixUpper === 'GRP'
        ? '그룹 쿠폰 뒷부분은 9자리여야 합니다.'
        : '쿠폰 코드 뒷부분은 7자리여야 합니다.');
      return;
    }

    if (fullCode.startsWith('GRP-') && hasCurrentActiveLicense()) {
      setPendingGroupCouponCode(fullCode);
      setShowCouponModal(false);
      return;
    }

    await submitCouponCode(fullCode, false);
  };

  // 1개월과 1년으로 플랜 그룹화
  const plansByDuration = plans.reduce((acc, plan) => {
    const key = plan.duration === 6 ? 1 : plan.duration;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(plan);
    return acc;
  }, {} as Record<1 | 12, LicensePlan[]>);

  // 플랜 타입 순서 정의
  const planTypeOrder: Array<'free' | 'basic' | 'standard' | 'pro'> = ['free', 'basic', 'standard', 'pro'];

  // 각 duration별로 플랜 타입 순서대로 정렬
  const sortedPlansByDuration = Object.entries(plansByDuration).reduce((acc, [duration, plans]) => {
    const sorted = plans.sort((a, b) => {
      const aIndex = planTypeOrder.indexOf(a.planType as any);
      const bIndex = planTypeOrder.indexOf(b.planType as any);
      return aIndex - bIndex;
    });
    acc[parseInt(duration) as 1 | 12] = sorted;
    return acc;
  }, {} as Record<1 | 12, LicensePlan[]>);

  const getPlanTypeName = (planType: string) => {
    switch (planType) {
      case 'free':
        return '프리';
      case 'basic':
        return '베이직';
      case 'standard':
        return '스탠다드';
      case 'pro':
        return '프로';
      case 'admin':
        return '관리자';
      default:
        return planType;
    }
  };

  const getPlanSubtitle = (planType: string): string | null => {
    switch (planType) {
      case 'free':
        return '(기본 과제 및 포인트 관리 추천)';
      case 'basic':
        return '(소규모 클래스 관리 추천)';
      case 'standard':
        return '(단독 클래스 관리 추천)';
      case 'pro':
        return '(다수 클래스 연동 관리 추천)';
      default:
        return null;
    }
  };

  const getCurrentPlan = () => {
    // 실제 활성 라이센스를 직접 확인
    const allLicenses = getAllLicensesArray();
    const activeLicense = allLicenses.find((lic: any) => lic.isActive !== false && lic.isRefunded !== true);
    
    if (activeLicense) {
      return plans.find(p => p.id === activeLicense.planId);
    }
    
    // 하위 호환: licenseInfo 또는 user.license 사용
    if (licenseInfo?.license) {
      return plans.find(p => p.id === licenseInfo.license?.planId);
    }
    
    if (effectiveUser.license?.planId) {
      return plans.find(p => p.id === effectiveUser.license?.planId);
    }
    
    return null;
  };

  // 플랜의 업그레이드 가격 계산: 이용권 전체 기간 중 오늘 제외, 내일~만료일 일수 × (상위 일금액 - 기존 일금액). 계산식 문자열도 반환.
  const calculateUpgradePriceForPlan = (plan: LicensePlan): { price: number; calculationLines: string[] } | null => {
    const allLicenses = getAllLicensesArray();
    const activeLicense = allLicenses.find((l: any) => l.isActive !== false && l.isRefunded !== true);
    if (!activeLicense) return null;

    const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
    const currentPlanType = activeLicense.planType;
    const isPlanUpgrade = !!currentPlanType && planOrder.indexOf(plan.planType) > planOrder.indexOf(currentPlanType);
    const isDurationUpgrade = 
      currentPlanType === plan.planType &&
      typeof activeLicense.duration === 'number' &&
      plan.duration > activeLicense.duration;
    if (!isPlanUpgrade && !isDurationUpgrade) return null;

    try {
      const periodEnd = getActivePeriodEndDate();
      if (!periodEnd) return null;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endDay = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate());
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const remainingDays = Math.max(0, Math.floor((endDay.getTime() - tomorrow.getTime()) / (24 * 60 * 60 * 1000)) + 1);

      const currentMonthly = MONTHLY_PRICE_KRW[currentPlanType] ?? 3600;
      const newMonthly = MONTHLY_PRICE_KRW[plan.planType] ?? 3600;
      const currentDaily = currentMonthly / 30;
      const newDaily = newMonthly / 30;
      const diffDaily = Math.max(0, newDaily - currentDaily);
      const price = Math.max(0, Math.round(diffDaily * remainingDays));
      const newPlanName = getPlanTypeName(plan.planType);
      const currentPlanName = getPlanTypeName(currentPlanType);
      const newDailyR = Math.round(newDaily);
      const currentDailyR = Math.round(currentDaily);
      const calculationLines = [
        `업그레이드 플랜(${newPlanName}) 일금액 : ${newDailyR.toLocaleString()}원`,
        `기존 플랜(${currentPlanName}) 일금액 : ${currentDailyR.toLocaleString()}원`,
        `(${newDailyR.toLocaleString()}원-${currentDailyR.toLocaleString()}원) × ${remainingDays}일 = ${price.toLocaleString()}원`,
      ];
      return { price, calculationLines };
    } catch (error) {
      console.error('업그레이드 가격 계산 실패:', error);
      return null;
    }
  };

  // 1년 기간 확장(동일 등급): 현재 시점부터 1년 이용기간 재설정, 확장된 날 수 × (할인가/365) = 차액만 결제
  // remainingDays >= 365 이면 구입 불가
  const calculateOneYearExtensionPrice = (plan: LicensePlan): { remainingDays: number; extendedDays: number; dailyRate: number; finalAmount: number; isRestricted: boolean } | null => {
    if (plan.duration !== 12) return null;
    const allLicenses = getAllLicensesArray();
    const activeLicense = allLicenses.find((l: any) => l.isActive !== false && l.isRefunded !== true);
    if (!activeLicense || !activeLicense.planType || activeLicense.planType === 'free') return null;
    const periodEnd = getActivePeriodEndDate();
    if (!periodEnd) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endDay = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate());
    const remainingDays = Math.max(0, Math.floor((endDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)) + 1);
    const isRestricted = remainingDays >= 365;
    const oneYearPrice = plan.price;
    const extendedDays = Math.max(0, 366 - remainingDays);
    const dailyRate = oneYearPrice / 365;
    const finalAmount = Math.round(extendedDays * dailyRate);
    return { remainingDays, extendedDays, dailyRate, finalAmount, isRestricted };
  };

  // 1년 탭 상위 플랜 업그레이드 가격: 1년 상위 플랜 가격 - (남은 기간에 해당하는 현재 플랜 가치)
  // 현재 플랜이 N개월 이용권이면: 일금액(월가격/30) × 남은 일수
  // 현재 플랜이 1년 이용권이면: 1년 플랜 가격 × (남은 일수/365)
  const calculateOneYearUpgradePrice = (plan: LicensePlan): { price: number; calculationLines: string[] } | null => {
    if (plan.duration !== 12) return null;
    const allLicenses = getAllLicensesArray();
    const activeLicense = allLicenses.find((l: any) => l.isActive !== false && l.isRefunded !== true);
    if (!activeLicense) return null;
    const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
    const currentPlanType = activeLicense.planType;
    if (planOrder.indexOf(plan.planType) <= planOrder.indexOf(currentPlanType)) return null;

    const periodEnd = getActivePeriodEndDate();
    if (!periodEnd) return null;
      const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endDay = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), periodEnd.getDate());
    const remainingDays = Math.max(0, Math.floor((endDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)) + 1);
    const oneYearPrice = plan.price;
    const isCurrentOneYearPlan = activeLicense.duration === 12;
    let remainingValue: number;
    const newPlanName = getPlanTypeName(plan.planType);
    const currentPlanName = getPlanTypeName(currentPlanType);
    let valueLine: string;

    if (isCurrentOneYearPlan) {
      const yearPlan = plans.find(p => p.planType === currentPlanType && p.duration === 12);
      const yearPlanPrice = yearPlan ? yearPlan.price : (MONTHLY_PRICE_KRW[currentPlanType] ?? 3600) * 12;
      remainingValue = Math.round((remainingDays / 365) * yearPlanPrice);
      valueLine = `현재 플랜(${currentPlanName}) 1년 이용권 가격 ${yearPlanPrice.toLocaleString()}원 × (${remainingDays}일/365일) = ${remainingValue.toLocaleString()}원`;
      } else {
      const monthly = MONTHLY_PRICE_KRW[currentPlanType] ?? 3600;
      const dailyRate = monthly / 30;
      remainingValue = Math.round(dailyRate * remainingDays);
      valueLine = `현재 플랜(${currentPlanName}) N개월 이용권 일금액 ${Math.round(dailyRate).toLocaleString()}원 × ${remainingDays}일 = ${remainingValue.toLocaleString()}원`;
    }

    const price = Math.max(0, oneYearPrice - remainingValue);
    const calculationLines = [
      `${newPlanName} 1년 이용권: ${oneYearPrice.toLocaleString()}원`,
      valueLine,
      `결제 금액: ${oneYearPrice.toLocaleString()}원 - ${remainingValue.toLocaleString()}원 = ${price.toLocaleString()}원`,
    ];
    return { price, calculationLines };
  };

  // 현재 활성화 플랜 이용기간(활성화된 이용권 연결)의 종료일. 업그레이드 남은 일수 계산에 사용
  const getActivePeriodEndDate = (): Date | null => {
    const allLicenses = getAllLicensesArray();
    const activated = allLicenses.filter((l: any) => l.isRefunded !== true && l.isActivated !== false);
    if (activated.length === 0) return null;
    let end: Date | null = null;
    for (const lic of activated) {
      const e = coerceToDate(lic.expiresAt);
      if (!end || e.getTime() > end.getTime()) end = e;
    }
    return end;
  };

  // 현재 활성 라이센스의 정보 가져오기 (planType, duration)
  const getCurrentActiveLicense = () => {
    const allLicenses = getAllLicensesArray();
    const activeLicense = allLicenses.find((lic: any) => lic.isActive !== false && lic.isRefunded !== true);
    
    if (activeLicense) {
      return {
        planType: activeLicense.planType,
        duration: activeLicense.duration,
      };
    }
    
    // 하위 호환: licenseInfo 또는 user.license 사용
    if (licenseInfo?.license) {
      return {
        planType: licenseInfo.license.planType,
        duration: licenseInfo.license.duration,
      };
    }
    
    if (effectiveUser.license?.planType && effectiveUser.license?.duration) {
      return {
        planType: effectiveUser.license.planType,
        duration: effectiveUser.license.duration,
      };
    }
    
    // 프리 플랜 또는 플랜이 없는 경우
    return {
      planType: effectiveUser.userType || 'free',
      duration: null as number | null,
    };
  };

  // 다운그레이드 여부 확인 (더 낮은 등급 또는 더 낮은 기간)
  const isDowngrade = (plan: LicensePlan): boolean => {
    const currentActive = getCurrentActiveLicense();
    
    // 현재 플랜이 없거나 프리 플랜이면 다운그레이드 불가능
    if (!currentActive.planType || currentActive.planType === 'free' || currentActive.planType === 'admin') {
      return false;
    }
    
    const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
    const currentPlanIndex = planOrder.indexOf(currentActive.planType);
    const targetPlanIndex = planOrder.indexOf(plan.planType);
    
    // 1. 더 낮은 등급의 플랜인지 확인
    if (targetPlanIndex < currentPlanIndex) {
      return true;
    }
    
    // 2. 같은 등급이지만 더 낮은 기간인지 확인 (1년 플랜 사용 중이면 1개월 플랜 비활성화)
    if (currentActive.duration === 12 && plan.duration === 1) {
      return true;
    }
    
    return false;
  };

  const getLicensePeriodText = () => {
    // 프리나 관리자 모드는 이용기간이 없으므로 무제한
    if (effectiveUser.userType === 'free' || effectiveUser.userType === 'admin') {
      return '무제한';
    }
    
    if (effectiveUser.license) {
      const rawPurchased = effectiveUser.license.purchasedAt;
      const rawExpires = effectiveUser.license.expiresAt;
      if (rawPurchased == null || rawExpires == null) {
        return '정보 없음';
      }
      const purchasedAt = rawPurchased instanceof Date ? rawPurchased : new Date(rawPurchased);
      const expiresAt = rawExpires instanceof Date ? rawExpires : new Date(rawExpires);
      if (isNaN(purchasedAt.getTime()) || isNaN(expiresAt.getTime())) {
        return '정보 없음';
      }
      
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}.${month}.${day}`;
      };
      
      return `${formatDate(purchasedAt)} ~ ${formatDate(expiresAt)}`;
    }
    
    return '정보 없음';
  };

  const currentPlan = getCurrentPlan();
  const currentUserType = effectiveUser.userType || 'free';
  const currentPlanName = currentPlan ? currentPlan.name : getPlanTypeName(currentUserType);
  void calculateUpgradeAmount;
  void createRefundMessage;
  void handleRefundRequest;
  void getLicensePeriodText;

  // 플랜 비교 표 데이터 (카테고리별로 그룹화)
  const comparisonData = [
    { category: '클래스 기본', feature: '클래스 생성', free: '1개', basic: '1개', standard: '2개', pro: '5개' },
    { category: '클래스 기본', feature: '멤버 그룹 생성 수', free: '기본 1개 그룹', basic: '기본 1개 그룹', standard: '최대 2개 그룹', pro: '최대 15개 그룹' },
    { category: '클래스 기본', feature: '멤버 수 (멤버 그룹별)', free: '30명', basic: '30명', standard: '35명', pro: '35명' },
    { category: '클래스 기본', feature: '추가 프로필 세트 사용\n(프로필 카드)', free: '불가', basic: '가능', standard: '가능', pro: '가능' },
    { category: '클래스 기본', feature: '아이템 카드 사용\n(레벨업, 과제보상, 스토어 카드)', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
    { category: '클래스 기본', feature: '멤버 개별 페이지 접근', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
    {
      category: '클래스 기본',
      feature: '스토리지 데이터 용량\n(과제 제출 이미지, 퀴즈 이미지)',
      free: '-',
      basic: '-',
      standard: '100MB',
      pro: '100MB'
    },
    { category: '클래스 기본', feature: '클래스 설정 연동', free: '불가', basic: '불가', standard: '불가', pro: '가능' },
    { category: '알람', feature: '알람 세트', free: '1개', basic: '3개', standard: '5개', pro: '10개' },
    { category: '알람', feature: '알람 종류', free: '기본 알람', basic: '모든 알람', standard: '모든 알람', pro: '모든 알람' },
    { category: '과제 관리', feature: '과제 생성 수 (월별)', free: '10개', basic: '무제한', standard: '무제한', pro: '무제한' },
    { category: '과제 관리', feature: '이용가능 과제 타입', free: '일반', basic: '일반, 루틴', standard: '일반, 루틴, 프로젝트', pro: '일반, 루틴, 프로젝트' },
    {
      category: '과제 관리',
      feature: '멤버 개별 과제 제출',
      free: '-',
      basic: '-',
      standard: '이미지, 링크, 메세지',
      pro: '이미지, 링크, 메세지'
    },
    { category: '클래스 챗', feature: '클래스챗 사용여부', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
    { category: '클래스 챗', feature: '문제 세트 수 (클래스당)', free: '-', basic: '-', standard: '10개', pro: '20개' },
    { category: '클래스 챗', feature: '문제 카드 수 (세트당)', free: '-', basic: '-', standard: '30개', pro: '30개' },
    { category: '보드', feature: '전체공지/개별공지 저장', free: '불가', basic: '불가', standard: '가능', pro: '가능' },
    { category: '보드', feature: '노트 카테고리 수', free: '1개', basic: '2개', standard: '5개', pro: '5개' },
  ];

  // 카테고리별로 그룹화하고 rowspan 계산
  const groupedData: Array<{ category: string; feature: string; free: string; basic: string; standard: string; pro: string; categoryRowspan?: number; isFirstInCategory?: boolean; isLastCategory?: boolean }> = [];
  const categoryCounts: { [key: string]: number } = {};
  
  // 카테고리별 개수 계산
  comparisonData.forEach(row => {
    categoryCounts[row.category] = (categoryCounts[row.category] || 0) + 1;
  });
  
  // groupedData 생성
  let currentCategory = '';
  const categories = Array.from(new Set(comparisonData.map(row => row.category)));
  const lastCategory = categories[categories.length - 1];
  comparisonData.forEach((row) => {
    const isFirstInCategory = row.category !== currentCategory;
    if (isFirstInCategory) {
      currentCategory = row.category;
    }
    groupedData.push({
      category: row.category,
      feature: row.feature,
      free: row.free,
      basic: row.basic,
      standard: row.standard,
      pro: row.pro,
      categoryRowspan: isFirstInCategory ? categoryCounts[row.category] : undefined,
      isFirstInCategory,
      isLastCategory: row.category === lastCategory && isFirstInCategory,
    });
  });

  if (loading) {
    return (
      <Container>
        <Header
          user={user}
          onLogout={onLogout}
          onNavigate={onNavigate}
          currentPage="license"
          onUpdateUser={onUpdateUser}
          onProfileModalOpen={onProfileModalOpen}
          onClassManageModalOpen={onClassManageModalOpen}
          classes={classes}
          currentClass={currentClass}
          memberGroups={memberGroups}
          currentMemberGroup={currentMemberGroup}
          onMemberGroupChange={onMemberGroupChange}
          themeMode={themeMode}
          onToggleTheme={onToggleTheme}
          onSeatAssignmentClick={onSeatAssignmentClick}
          onLevelSettingsClick={onLevelSettingsClick}
          onPointManagementClick={onPointManagementClick}
          onItemCardSettingsClick={onItemCardSettingsClick}
          onAssignmentSettingsClick={onAssignmentSettingsClick}
        />
        <Content>
          <ContentWrapper>
          <LoadingMessage>이용권 정보를 불러오는 중...</LoadingMessage>
          </ContentWrapper>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header
        user={user}
        onLogout={onLogout}
        onNavigate={onNavigate}
        currentPage="license"
        onUpdateUser={onUpdateUser}
        onProfileModalOpen={onProfileModalOpen}
        onClassManageModalOpen={onClassManageModalOpen}
        classes={classes}
        currentClass={currentClass}
        memberGroups={memberGroups}
        currentMemberGroup={currentMemberGroup}
        onMemberGroupChange={onMemberGroupChange}
        themeMode={themeMode}
        onToggleTheme={onToggleTheme}
        onSeatAssignmentClick={onSeatAssignmentClick}
        onLevelSettingsClick={onLevelSettingsClick}
        onPointManagementClick={onPointManagementClick}
        onItemCardSettingsClick={onItemCardSettingsClick}
        onAssignmentSettingsClick={onAssignmentSettingsClick}
      />
      <Content>
        <ContentWrapper>
        {error && <ErrorMessage>{error}</ErrorMessage>}

          {/* 현재 이용중인 플랜 정보 카드 */}
          {(() => {
            const allLicenses = getAllLicensesArray();
            // 활성화된 이용권 전체(환불 제외)를 합쳐 이용기간 산정: min(시작일) ~ max(종료일)
            const activatedLicenses = allLicenses.filter(
              (lic: any) => lic.isRefunded !== true && lic.isActivated !== false
            );
            
            const formatDate = (date: Date) => {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              return `${year}.${month}.${day}`;
            };
            
            let periodStart: Date | null = null;
            let periodEnd: Date | null = null;
            if (activatedLicenses.length > 0) {
              for (const lic of activatedLicenses) {
                const start = toDateSafe(lic.purchasedAt);
                const end = toDateSafe(lic.expiresAt);
                if (!periodStart || start.getTime() < periodStart.getTime()) periodStart = start;
                if (!periodEnd || end.getTime() > periodEnd.getTime()) periodEnd = end;
              }
            }

            const periodText =
              periodStart && periodEnd ? `${formatDate(periodStart)} ~ ${formatDate(periodEnd)}` : null;
            const periodPlaceholder = '0000.00.00 ~ 0000.00.00';
            
            return (
              <CurrentLicenseInfoCard>
                <InfoSection>
                  <InfoLabel>현재 활성화 플랜 이용기간</InfoLabel>
                  <CurrentPlanPeriodCenter>
                    <CurrentPlanPeriodRange>{periodText ?? periodPlaceholder}</CurrentPlanPeriodRange>
                  </CurrentPlanPeriodCenter>
                  <CurrentPlanPeriodLabel>이용권</CurrentPlanPeriodLabel>
                  <PlanNameRow>
                    {displayAsGroupLicense((effectiveUser as any)?.license) && (
                      <CouponBadge>그룹 이용권</CouponBadge>
                    )}
                    {displayAsAdminIssuedLicense((effectiveUser as any)?.license) && (
                      <AdminIssuedBadge>관리자 발급</AdminIssuedBadge>
                    )}
                    {displayAsCouponLicense((effectiveUser as any)?.license) && !displayAsGroupLicense((effectiveUser as any)?.license) && (
                      <CouponBadge>이벤트 쿠폰</CouponBadge>
                    )}
                    <PlanNameLarge>{currentPlanName}</PlanNameLarge>
                  </PlanNameRow>
                </InfoSection>
              </CurrentLicenseInfoCard>
            );
          })()}

          {/* 이용권 구매 이력 - 항상 표시 */}
          {(() => {
            const licensesArray = getLicensesArray();
            return (
              <LicenseHistoryCard>
                <LicenseHistoryHeader>
                  <LicenseHistoryTitle>이용권 구매 이력</LicenseHistoryTitle>
                  <DetailHistoryButton onClick={() => setShowAllLicenses(!showAllLicenses)}>
                    {showAllLicenses ? '[상세 결제 내역 닫기]' : '[상세 결제 내역 확인]'}
                  </DetailHistoryButton>
                </LicenseHistoryHeader>
                <LicenseHistoryList>
                  {licensesArray.length === 0 ? (
                    <EmptyLicenseBlock>
                      <EmptyLicenseText>현재 이용중인 이용권이 없습니다.</EmptyLicenseText>
                    </EmptyLicenseBlock>
                  ) : (() => {
                    const isActivatedLicense = (l: any) => l.isActivated !== false;
                    const isRefundedOrExpired = (l: any) => l.isRefunded === true || (isActivatedLicense(l) && isLicenseExpired(l));
                    const getActivatedSortTime = (l: any) => {
                      const at = l.activatedAt ? toDateSafe(l.activatedAt).getTime() : 0;
                      if (at > 0) return at;
                      const fallback = l.periodStart ?? l.paidAt ?? l.purchasedAt;
                      return fallback ? toDateSafe(fallback).getTime() : 0;
                    };
                    const getPurchasedSortTime = (l: any) => {
                      const pa = l.paidAt ?? l.purchasedAt;
                      return pa ? toDateSafe(pa).getTime() : 0;
                    };
                    const group1Active = [...licensesArray].filter((l: any) => isActivatedLicense(l) && !isRefundedOrExpired(l)).sort((a: any, b: any) => getActivatedSortTime(b) - getActivatedSortTime(a));
                    const group2Inactive = [...licensesArray].filter((l: any) => !isActivatedLicense(l) && !isRefundedOrExpired(l)).sort((a: any, b: any) => getPurchasedSortTime(b) - getPurchasedSortTime(a));
                    const group3Done = [...licensesArray].filter(isRefundedOrExpired).sort((a: any, b: any) => getPurchasedSortTime(b) - getPurchasedSortTime(a));
                    const sorted = [...group1Active, ...group2Inactive, ...group3Done];
                    const nonRefunded = sorted.filter((l: any) => l.isRefunded !== true);
                    const activatedNonRefunded = nonRefunded.filter((l: any) => l.isActivated !== false);
                    const latestActivatedRefundable = activatedNonRefunded[0] ?? null;
                    const latestActivatedRefundablePaymentId = latestActivatedRefundable?.paymentId ?? null;
                    const activeLicense = licensesArray.find((l: any) => l.isActive !== false && l.isRefunded !== true && l.isActivated !== false);
                    const activeIsYear = activeLicense && (activeLicense.duration === 12 || String(activeLicense.planId || '').endsWith('-12'));

                    return sorted.map((license: any, index: number) => {
                    const formatDate = (date: Date | undefined) => {
                      if (!date || isNaN(new Date(date).getTime())) return '-';
                      const d = date instanceof Date ? date : new Date(date);
                      const year = d.getFullYear();
                      const month = String(d.getMonth() + 1).padStart(2, '0');
                      const day = String(d.getDate()).padStart(2, '0');
                      return `${year}.${month}.${day}`;
                    };
                    const isActivated = license.isActivated !== false;
                    const paidAmount = license.amount || 0;
                    const planTypeName = getPlanTypeName(license.planType);
                    const durationNum = typeof license.duration === 'number' ? license.duration : 1;
                    const durationText = durationNum === 12 ? '1년' : `${durationNum}개월`;
                    const licenseBadgeText = license.purchaseType === 'upgrade'
                      ? `${planTypeName} - 업그레이드`
                      : license.isExtension === true
                        ? `${planTypeName} - ${durationText} - 기간 연장`
                        : `${planTypeName} - ${durationText}`;

                    const purchaseDateForDisplay = license.paidAt ?? license.purchasedAt;
                    const periodStartForDisplay = license.periodStart ?? license.purchasedAt;
                    const licenseIsYear = durationNum === 12 || String(license.planId || '').endsWith('-12');
                    const cannotActivateByMix = !isActivated && activeLicense && ((licenseIsYear && !activeIsYear) || (!licenseIsYear && activeIsYear));
                    const isExtensionLicense = license.isExtension === true;
                    const hasActivatedLicense = !!activeLicense;
                    const cannotActivateByExisting = !isActivated && hasActivatedLicense && !isExtensionLicense;
                    const isRefunded = license.isRefunded === true;
                                    const isCouponLicense = license.paymentId && String(license.paymentId).startsWith('COUPON-');
                                    const proofUnavailable = !!(isCouponLicense || displayAsAdminIssuedLicense(license));
                                    const activateDisabled = isRefunded || isProcessingActivation[license.paymentId] === true || cannotActivateByMix || cannotActivateByExisting;
                                    const activateButtonText = isExtensionLicense ? '기간 연장 활성화' : '이용권 활성화';

                                    if (!isActivated) {
                    return (
                        <LicenseHistoryBlock key={license.paymentId} $isRefunded={isRefunded} $isExpired={false}>
                          <PurchaseDateBadge>구매일 {formatDate(purchaseDateForDisplay)}</PurchaseDateBadge>
                        <LicenseHistoryLeft>
                            <LicenseHistoryLabel>이용 기간</LicenseHistoryLabel>
                            <LicensePeriodDateText>활성화 전</LicensePeriodDateText>
                            <LicensePaymentInfoText>
                              결제금액: {paidAmount.toLocaleString()}원
                            </LicensePaymentInfoText>
                            <LicenseBadgeRow>
                              {displayAsGroupLicense(license) && <CouponBadge>그룹 이용권</CouponBadge>}
                              {displayAsAdminIssuedLicense(license) && <AdminIssuedBadge>관리자 발급</AdminIssuedBadge>}
                              {displayAsCouponLicense(license) && !displayAsGroupLicense(license) && <CouponBadge>이벤트 쿠폰</CouponBadge>}
                              <LicenseDurationBadge>{licenseBadgeText}</LicenseDurationBadge>
                            </LicenseBadgeRow>
                            <RefundButtonRow>
                              <RefundRequestButton
                                onClick={() => !isRefunded && handleActivateLicense(license)}
                                disabled={activateDisabled}
                                $isNotEligible={activateDisabled}
                                title={isRefunded ? '환불된 이용권은 활성화할 수 없습니다.' : cannotActivateByExisting ? '이미 활성화된 이용권이 있습니다. 다른 이용권을 활성화할 수 없습니다.' : cannotActivateByMix ? (licenseIsYear ? '1년 이용권은 N개월 이용권이 활성화되어 있지 않을 때만 활성화할 수 있습니다.' : 'N개월 이용권은 1년 이용권이 활성화되어 있지 않을 때만 활성화할 수 있습니다.') : undefined}
                              >
                                {isProcessingActivation[license.paymentId] ? '활성화 중...' : activateButtonText}
                              </RefundRequestButton>
                            </RefundButtonRow>
                            {cannotActivateByExisting && (
                              <PendingActivationNoticeWrapper>
                                <PendingActivationNotice>
                                  <div>이미 활성화된 이용권이 있습니다. 다른 이용권을 활성화하려면 먼저 해당 이용권을 환불하거나 만료될 때까지 이용해 주세요.</div>
                                </PendingActivationNotice>
                              </PendingActivationNoticeWrapper>
                            )}
                            {!cannotActivateByExisting && cannotActivateByMix && (
                              <PendingActivationNoticeWrapper>
                                <PendingActivationNotice>
                                  <div>{licenseIsYear ? '1년 이용권은 N개월 이용권이 활성화되어 있지 않을 때만 활성화할 수 있습니다.' : 'N개월 이용권은 1년 이용권이 활성화되어 있지 않을 때만 활성화할 수 있습니다.'}</div>
                                </PendingActivationNotice>
                              </PendingActivationNoticeWrapper>
                            )}
                            {!activateDisabled && (
                            <PendingActivationNoticeWrapper>
                              <PendingActivationNotice>
                                <div>{isExtensionLicense ? '*기간 연장 이용권을 활성화하면 이용권에 기록된 기간만큼 기존 이용기간에 추가되어 재설정 됩니다.' : '*이용권을 활성화하면, 활성화한 날부터 이용기간이 시작되며 구매한 플랜의 기간만큼 적용됩니다.'}</div>
                              </PendingActivationNotice>
                            </PendingActivationNoticeWrapper>
                            )}
                          </LicenseHistoryLeft>
                          <LicenseHistoryDivider />
                          <LicenseHistoryMiddle>
                            {isRefunded ? (
                              <>
                                <LicenseHistoryLabel>환불 완료</LicenseHistoryLabel>
                                <LicensePeriodDateText>
                                  {license.refundedAt ? (license.refundedAt instanceof Date ? formatDate(license.refundedAt) : new Date((license.refundedAt as any)?.toDate?.() ?? license.refundedAt).toLocaleDateString('ko-KR')) : '-'}
                                </LicensePeriodDateText>
                                <RefundButtonRow>
                                  <RefundRequestButton disabled $isNotEligible>
                                    환불 요청 완료
                                  </RefundRequestButton>
                                </RefundButtonRow>
                              </>
                            ) : (
                              <>
                                <LicenseHistoryLabel>환불 가능 기간</LicenseHistoryLabel>
                                <LicensePeriodDateText>
                                  {(() => {
                                    const { refundEligibleFrom, refundEligibleUntil } = getRefundEligibility(license as any);
                                    return refundEligibleFrom && refundEligibleUntil ? `${formatDate(refundEligibleFrom)} ~ ${formatDate(refundEligibleUntil)}` : '-';
                                  })()}
                                </LicensePeriodDateText>
                                <RefundButtonRow style={{ marginBottom: 12 }}>
                                  {isCouponLicense ? (
                                    <RefundRequestButton disabled $isNotEligible style={{ background: 'var(--color-background)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}>
                                      환불 불가 (쿠폰 이용권)
                                    </RefundRequestButton>
                                  ) : (
                                    <RefundRequestButton
                                      onClick={() => handleRefundUnactivatedLicense(license)}
                                      disabled={isProcessingRefund[license.paymentId] === true}
                                      $isNotEligible={isProcessingRefund[license.paymentId] === true}
                                      style={{ background: 'var(--color-background)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }}
                                    >
                                      {isProcessingRefund[license.paymentId] ? '처리 중...' : '이용권 환불'}
                                    </RefundRequestButton>
                                  )}
                                </RefundButtonRow>
                                {!isCouponLicense && (
                                  <PendingActivationNoticeWrapper>
                                    <PendingActivationNotice>
                                      <div>*이용권은 구매일로부터 7일 이내에만 환불 가능합니다. 환불 시 취소·운영 수수료 10%와 해당 이용권 결제금액의 일금액×사용일수를 제외한 잔액이 환불됩니다.</div>
                                    </PendingActivationNotice>
                                  </PendingActivationNoticeWrapper>
                                )}
                              </>
                            )}
                          </LicenseHistoryMiddle>
                          <LicenseHistoryDivider />
                          <LicenseHistoryProof>
                            <LicenseHistoryLabel>증빙서류</LicenseHistoryLabel>
                            <ProofButtonStack>
                              <ProofDocButton
                                type="button"
                                disabled={proofUnavailable}
                                onClick={() => !proofUnavailable && setLicenseProofModal({ source: 'license', license })}
                              >
                                구매 영수증
                              </ProofDocButton>
                            </ProofButtonStack>
                            {proofUnavailable && (
                              <ProofSmallPrint>카드 결제가 없는 건(쿠폰·관리자 발급 등)은 증빙을 제공하지 않습니다.</ProofSmallPrint>
                            )}
                          </LicenseHistoryProof>
                        </LicenseHistoryBlock>
                      );
                    }

                    const periodText = `${formatDate(periodStartForDisplay)} ~ ${formatDate(license.expiresAt)}`;
                    const isExpired = isLicenseExpired(license);
                    const activatedRefund = getRefundAmountKrwForActivated(paidAmount, periodStartForDisplay, durationNum, { isExtension: license.isExtension === true, periodEndDate: license.expiresAt });
                    const { isEligible: refundWithin7Days, refundEligibleFrom, refundEligibleUntil } = getRefundEligibility(license as any);
                    const canRefundActivated = activatedRefund.refundKrw > 0 && refundWithin7Days;

                    const isDone = license.isRefunded === true || isExpired;
                    return (
                      <LicenseHistoryBlock key={license.paymentId} $isRefunded={license.isRefunded === true} $isExpired={isExpired} $isActivated>
                        <PurchaseDateBadge>구매일 {formatDate(purchaseDateForDisplay)}</PurchaseDateBadge>
                        <LicenseHistoryLeft>
                          <LicenseHistoryLabel>{isExpired ? '이용 기간 만료' : '이용 기간'}</LicenseHistoryLabel>
                          <LicensePeriodDateText>{periodText}</LicensePeriodDateText>
                          <LicensePaymentInfoText>
                            결제금액: {paidAmount.toLocaleString()}원
                          </LicensePaymentInfoText>
                          <LicenseBadgeRow>
                            {displayAsGroupLicense(license) && <CouponBadge>그룹 이용권</CouponBadge>}
                            {displayAsAdminIssuedLicense(license) && <AdminIssuedBadge>관리자 발급</AdminIssuedBadge>}
                            {displayAsCouponLicense(license) && !displayAsGroupLicense(license) && <CouponBadge>이벤트 쿠폰</CouponBadge>}
                            <LicenseDurationBadge>{licenseBadgeText}</LicenseDurationBadge>
                          </LicenseBadgeRow>
                        </LicenseHistoryLeft>
                        <LicenseHistoryDivider />
                        <LicenseHistoryMiddle>
                          {license.isRefunded ? (
                            <>
                              <LicenseHistoryLabel>환불 완료</LicenseHistoryLabel>
                              <LicensePeriodDateText>
                                {license.refundedAt instanceof Date ? formatDate(license.refundedAt) : new Date((license.refundedAt as any)?.toDate?.() ?? license.refundedAt).toLocaleDateString('ko-KR')}
                              </LicensePeriodDateText>
                            </>
                          ) : displayAsGroupLicense(license) ? (
                            <PendingActivationNoticeWrapper>
                              <LicenseHistoryLabel>환불 안내</LicenseHistoryLabel>
                              <PendingActivationNotice style={{ marginTop: 8 }}>
                                <div>그룹 이용권으로 그룹 이용권 구매자가 그룹 이용권 전체를 일괄 취소·환불 진행을 할 수 있습니다. 그룹 이용권 구매자에게 문의하세요.</div>
                              </PendingActivationNotice>
                            </PendingActivationNoticeWrapper>
                          ) : (
                            <>
                              <LicenseHistoryLabel>환불 가능 기간</LicenseHistoryLabel>
                              <LicensePeriodDateText>
                                {refundEligibleFrom && refundEligibleUntil ? `${formatDate(refundEligibleFrom)} ~ ${formatDate(refundEligibleUntil)}` : '-'}
                              </LicensePeriodDateText>
                              {canRefundActivated ? (
                                <>
                                  <LicensePaymentInfoText>예상 환불 금액 : {activatedRefund.refundKrw.toLocaleString()}원</LicensePaymentInfoText>
                          <RefundButtonRow>
                            <RefundRequestButton
                                      onClick={() => setRefundModalLicense(license)}
                                      disabled={isProcessingRefund[license.paymentId] || license.paymentId !== latestActivatedRefundablePaymentId}
                                      $isNotEligible={license.paymentId !== latestActivatedRefundablePaymentId}
                                    >
                                      {isProcessingRefund[license.paymentId] ? '처리 중...' : license.paymentId !== latestActivatedRefundablePaymentId ? '환불 요청' : '환불 요청'}
                            </RefundRequestButton>
                          </RefundButtonRow>
                                </>
                              ) : (
                                <>
                                  <LicensePaymentInfoText>환불 불가</LicensePaymentInfoText>
                                  <RefundButtonRow>
                                    <RefundRequestButton disabled $isNotEligible>
                                      환불 요청 불가
                                    </RefundRequestButton>
                                  </RefundButtonRow>
                                </>
                              )}
                              {!isDone && license.paymentId !== latestActivatedRefundablePaymentId && (
                                <PendingActivationNoticeWrapper>
                                  <PendingActivationNotice>
                                    <div>*플랜에 적용된 가장 최근 활성화된 이용권을 먼저 환불해야 해당 이용권 환불이 가능합니다.</div>
                                  </PendingActivationNotice>
                                </PendingActivationNoticeWrapper>
                              )}
                            </>
                          )}
                        </LicenseHistoryMiddle>
                        <LicenseHistoryDivider />
                        <LicenseHistoryProof>
                          <LicenseHistoryLabel>증빙서류</LicenseHistoryLabel>
                          <ProofButtonStack>
                            <ProofDocButton
                              type="button"
                              disabled={proofUnavailable}
                              onClick={() => !proofUnavailable && setLicenseProofModal({ source: 'license', license })}
                            >
                              구매 영수증
                            </ProofDocButton>
                          </ProofButtonStack>
                          {proofUnavailable && (
                            <ProofSmallPrint>카드 결제가 없는 건(쿠폰·관리자 발급 등)은 증빙을 제공하지 않습니다.</ProofSmallPrint>
                          )}
                        </LicenseHistoryProof>
                      </LicenseHistoryBlock>
                    );
                    });
                  })()}
                </LicenseHistoryList>
                {(() => {
                  // 환불되지 않은 모든 라이센스의 결제 금액 합계 계산
                  const allLicenses = getAllLicensesArray();
                  const totalPaidAmount = allLicenses
                    .filter((lic: any) => lic.isRefunded !== true)
                    .reduce((sum: number, lic: any) => sum + (lic.amount || 0), 0);
                  
                  return (
                    <LicenseTotalAmountText>
                      총 서비스 이용 결재액 : {totalPaidAmount.toLocaleString()}원
                    </LicenseTotalAmountText>
                  );
                })()}
              </LicenseHistoryCard>
            );
          })()}

          {groupCouponBatches.length > 0 && (
          <LicenseHistoryCard>
            <LicenseHistoryHeader>
              <LicenseHistoryTitle>그룹 이용권 구매 이력</LicenseHistoryTitle>
              {groupCouponBatches.length >= 2 && (
                <DetailHistoryButton onClick={() => setShowAllGroupCoupons(!showAllGroupCoupons)}>
                  {showAllGroupCoupons ? '[상세 결제 내역 닫기]' : '[상세 결제 내역 확인]'}
                </DetailHistoryButton>
              )}
            </LicenseHistoryHeader>
            <LicenseHistoryList>
              {(showAllGroupCoupons ? groupCouponBatches : groupCouponBatches.slice(0, 1)).map((batch) => {
                  const activationValidUntilYmd = getGroupActivationValidUntilYmd(batch);
                  const refundInfo = getGroupRefundEligibility(batch);
                  const isRefunded = Number(batch.refundedCount || 0) >= Number(batch.quantity || 0) || batch.status === 'REFUNDED';
                  const planLabel = `${getPlanTypeName(batch.planType)} - ${batch.termMonths === 12 ? '1년' : `${batch.termMonths}개월`}`;
                  const refundButtonText = isRefunded
                    ? '환불 완료'
                    : refundInfo.isEligible
                      ? '환불 요청'
                      : '환불 불가';
                  const groupProofUnavailable = batch.adminIssued === true || batch.paymentMode === 'test';
                  return (
                    <LicenseHistoryBlock
                      key={batch.batchId}
                      $isRefunded={isRefunded}
                      $isExpired={false}
                      $isActivated={Number(batch.activatedCount || 0) > 0}
                    >
                      <PurchaseDateBadge>구매일 {formatDateForDisplay(batch.createdAt)}</PurchaseDateBadge>
                      <LicenseHistoryLeft>
                        <LicenseHistoryLabel>그룹 쿠폰 활성화 기간</LicenseHistoryLabel>
                        <LicensePeriodDateText>
                          {`${formatDateForDisplay(batch.createdAt)} ~ ${formatYmdToDisplay(activationValidUntilYmd)}`}
                        </LicensePeriodDateText>
                        <LicensePaymentInfoText>구매 이용권 수 : {Number(batch.quantity || 0)}개</LicensePaymentInfoText>
                        <ActivatedCountBlock>
                          활성: {Number(batch.activatedCount || 0)} / {Number(batch.quantity || 0)}
                        </ActivatedCountBlock>
                        <LicensePaymentInfoText>결제금액 : {Number(batch.totalAmountKrw || 0).toLocaleString()}원</LicensePaymentInfoText>
                        <LicenseBadgeRow>
                          <CouponBadge>그룹 이용권</CouponBadge>
                          <LicenseDurationBadge>{planLabel}</LicenseDurationBadge>
                        </LicenseBadgeRow>
                      </LicenseHistoryLeft>
                      <LicenseHistoryDivider />
                      <LicenseHistoryMiddle>
                        <LicenseHistoryLabel>쿠폰 받기 이메일</LicenseHistoryLabel>
                        <EmailRow>
                          <EmailStatusIndicator $success={batch.emailStatus === 'SENT'} aria-label={batch.emailStatus === 'SENT' ? '전송 완료' : '전송 미완료 또는 실패'}>
                            {batch.emailStatus === 'SENT' ? (
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            ) : (
                              <span>!</span>
                            )}
                          </EmailStatusIndicator>
                          <LicensePeriodDateText>{batch.targetEmail || '-'}</LicensePeriodDateText>
                        </EmailRow>
                        <LicensePaymentInfoText>쿠폰 코드 : <strong>{batch.groupCode || batch.batchId || '-'}</strong></LicensePaymentInfoText>
                        <LicenseHistoryLabel>환불 가능 기간</LicenseHistoryLabel>
                        <LicensePeriodDateText>
                          {`${formatDateForDisplay(batch.createdAt)} ~ ${formatYmdToDisplay(batch.refundEligibleUntilYmd)}`}
                        </LicensePeriodDateText>
                        <RefundButtonRow>
                          <RefundRequestButton
                            onClick={() => handleGroupCouponRefundRequest(batch)}
                            disabled={!refundInfo.isEligible || isProcessingGroupRefund[batch.batchId] === true || isRefunded}
                            $isNotEligible={!refundInfo.isEligible || isRefunded}
                          >
                            {isProcessingGroupRefund[batch.batchId] ? '환불 처리 중...' : refundButtonText}
                          </RefundRequestButton>
                        </RefundButtonRow>
                      </LicenseHistoryMiddle>
                      <LicenseHistoryDivider />
                      <LicenseHistoryProof>
                        <LicenseHistoryLabel>증빙서류</LicenseHistoryLabel>
                        <ProofButtonStack>
                          <ProofDocButton
                            type="button"
                            disabled={groupProofUnavailable}
                            onClick={() => !groupProofUnavailable && setLicenseProofModal({ source: 'group', batch })}
                          >
                            구매 영수증
                          </ProofDocButton>
                        </ProofButtonStack>
                        {groupProofUnavailable && (
                          <ProofSmallPrint>테스트·관리자 발급 등 실제 카드 결제가 없는 건은 증빙을 제공하지 않습니다.</ProofSmallPrint>
                        )}
                      </LicenseHistoryProof>
                    </LicenseHistoryBlock>
                  );
                })}
            </LicenseHistoryList>
          </LicenseHistoryCard>
          )}

          {/* 환불 안내 메시지 - 구매 모드에 따라 표시 */}
          <RefundInfoSection>
            <RefundInfoTitle>{purchaseMode === 'group' ? '그룹 이용권 구입' : '이용권 구입'}</RefundInfoTitle>
            <RefundInfoMessage>
              {purchaseMode === 'group' ? (
                <>
                  그룹 이용권은 구매일로부터 7일 이내에 한하여 구매자가 환불을 신청할 수 있습니다.
                  <br />
                  환불 시 미활성 이용권은 전액 환불되나, 활성화된 이용권은 10%의 운영 수수료와 사용일수만큼 차감 후 잔액이 지급됩니다.
                  <br />
                  그룹 이용권 특성상 부분 취소는 불가능하며 전체 수량이 일괄 취소됩니다.
                  <br />
                  또한, 모든 코드는 구매 후 한 달 이내에만 활성화할 수 있으므로,
                  <br />
                  미사용 권한이 소멸되지 않도록 기간 내 등록을 완료해 주시기 바랍니다.
                </>
              ) : (
                <>
                  이용권(미활성/활성 공통): 구매일로부터 7일 이내에만 환불 가능.
                  <br />
                  환불 시 취소·운영 수수료 10%와 해당 이용권 결제금액의 일금액×사용일수(오늘 포함)를 제외한 잔액이 환불됩니다.
                  <br />
                  이용권 이용기간은 이용권 쿠폰 등록 또는 이용권 구매 즉시 적용됩니다.
                  <br />
                  1개월 플랜에서 1년 플랜으로 업그레이드 할 시 기존 유료 플랜 이용 시작일 부터 1년 동안을 이용 기간으로 업그레이드 됩니다.
                </>
              )}
            </RefundInfoMessage>
          </RefundInfoSection>

          {/* 쿠폰 등록 버튼 */}
          <CouponButtonContainer>
            <CouponButton onClick={() => setShowCouponModal(true)}>
              쿠폰 코드 등록
            </CouponButton>
          </CouponButtonContainer>

          <PurchaseModeToggleContainer>
            <PurchaseModeToggleButton
              $isActive={purchaseMode === 'single'}
              onClick={() => setPurchaseMode('single')}
            >
              단일 이용자 구매
            </PurchaseModeToggleButton>
            <PurchaseModeToggleButton
              $isActive={purchaseMode === 'group'}
              onClick={() => setPurchaseMode('group')}
            >
              그룹 이용자 구매
            </PurchaseModeToggleButton>
          </PurchaseModeToggleContainer>

          {/* 카테고리 탭 버튼 */}
          <TabContainer>
            <TabButton
              $isActive={selectedDuration === 1}
              onClick={() => setSelectedDuration(1)}
            >
              {purchaseMode === 'group' ? '그룹 N개월 이용권' : 'N개월 이용권'}
            </TabButton>
            <TabButton
              $isActive={selectedDuration === 12}
              onClick={() => setSelectedDuration(12)}
            >
              {purchaseMode === 'group' ? '그룹 1년 이용권' : '1년 이용권'}
            </TabButton>
          </TabContainer>

          {/* 선택된 기간의 플랜 표시 */}
          {sortedPlansByDuration[selectedDuration] && sortedPlansByDuration[selectedDuration].length > 0 && (
            <CategorySection>
              {!effectivePaidPaymentEnabled && (
                <BetaTestNotice>
                  *현재는 베타 테스트 기간으로 유료 결제가 불가능 합니다. 이용 쿠폰 코드를 통해 플랜 권한을 획득해주세요.
                </BetaTestNotice>
              )}
              {purchaseMode === 'single' && (() => {
                const currentActiveForNotice = getCurrentActiveLicense();
                const isOnYearTab = selectedDuration === 12;
                const isOnNMonthTab = selectedDuration === 1;
                const activeIsYear = !!currentActiveForNotice.planType && currentActiveForNotice.planType !== 'free' && currentActiveForNotice.duration === 12;
                const activeIsNMonth = !!currentActiveForNotice.planType && currentActiveForNotice.planType !== 'free' && currentActiveForNotice.duration !== 12 && currentActiveForNotice.duration != null;
                if (isOnYearTab && activeIsNMonth) {
                  return (
                    <PlanDurationNotice>
                      현재 {currentActiveForNotice.duration}개월 이용권을 이용중입니다. {currentActiveForNotice.duration}개월 이용권 만료 후 1년 이용권을 구매, 적용할 수 있습니다.
                    </PlanDurationNotice>
                  );
                }
                if (isOnNMonthTab && activeIsYear) {
                  return (
                    <PlanDurationNotice>
                      현재 1년 이용권을 이용중입니다. 1년 이용권 만료 후 N개월 이용권을 구매, 적용할 수 있습니다.
                    </PlanDurationNotice>
                  );
                }
                return null;
              })()}
              <PlanRow>
                {sortedPlansByDuration[selectedDuration].map((plan) => {
                  // 현재 플랜 체크: 정확히 일치하거나, 프리 플랜이고 현재 플랜이 없거나 프리인 경우
                  const isCurrentPlan = 
                    currentPlan?.id === plan.id || 
                    (plan.planType === 'free' && (!currentPlan || currentPlan.planType === 'free'));
                  const isNMonthTab = selectedDuration === 1;
                  const isOneYearTab = selectedDuration === 12;
                  const currentActive = getCurrentActiveLicense();
                  const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
                  const isHigherPlan = !!currentActive.planType && currentActive.planType !== 'free' && planOrder.indexOf(plan.planType) > planOrder.indexOf(currentActive.planType);
                  const activeIsYear = !!currentActive.planType && currentActive.planType !== 'free' && currentActive.duration === 12;
                  // 1년 활성 시 N개월 탭 비활성화, N개월 활성 시 1년 탭 비활성화. 다운그레이드 여부와 합쳐서 비활성화 판단.
                  const isDisabledByOtherDuration = (isOneYearTab && !activeIsYear && !!currentActive.planType && currentActive.planType !== 'free') || (isNMonthTab && activeIsYear);
                  const isDisabledByBeta = !effectivePaidPaymentEnabled && plan.planType !== 'free';
                  const isDisabledInSingleMode = isDowngrade(plan) || (plan.planType !== 'free' && isDisabledByOtherDuration) || isDisabledByBeta;
                  const isDisabledInGroupMode = isDisabledByBeta || plan.planType === 'free';
                  const isDisabled = purchaseMode === 'group' ? isDisabledInGroupMode : isDisabledInSingleMode;
                  const handleCardClick = () => {
                    if (isDisabled) return;
                    if (purchaseMode === 'group') {
                      setNMonthModalPlan(plan);
                      setOneYearModalIntent(null);
                    } else if (isNMonthTab && plan.planType !== 'free') {
                      setNMonthModalPlan(plan);
                      setOneYearModalIntent(null);
                    } else if (isOneYearTab && plan.planType !== 'free') {
                      setNMonthModalPlan(plan);
                      setOneYearModalIntent(activeIsYear && isCurrentPlan ? 'extension' : activeIsYear && isHigherPlan ? 'upgrade' : 'purchase');
                    } else {
                      handlePurchase(plan);
                    }
                  };
                  return (
                    <PlanCard
                      key={plan.id}
                      $isCurrent={isCurrentPlan}
                      $isDisabled={isDisabled}
                      onClick={handleCardClick}
                    >
                      {isCurrentPlan && !isDisabledByBeta && (
                        <CurrentRibbon>이용중</CurrentRibbon>
                      )}
                      <PlanTypeName>{getPlanTypeName(plan.planType)}</PlanTypeName>
                      {getPlanSubtitle(plan.planType) && (
                        <PlanSubtitle>{getPlanSubtitle(plan.planType)}</PlanSubtitle>
                      )}
                      <PlanPriceContainer>
                        {(() => {
                          const isYearPlan = plan.duration === 12;
                          const oneYearDiscount = isYearPlan && plan.originalPrice != null && plan.originalPrice > plan.price && (
                            <>
                              <PlanPriceOriginalRow>
                                <PlanPriceLabel>연</PlanPriceLabel>
                                <PlanPriceStrikethrough>
                                  {licenseService.formatPrice(plan.originalPrice, plan.currency)}
                                </PlanPriceStrikethrough>
                              </PlanPriceOriginalRow>
                              <PlanPriceDivider />
                            </>
                          );
                          const priceEl = isYearPlan ? (
                                <PlanPrice>
                              <PlanPriceLabel>할인가</PlanPriceLabel>
                                  {licenseService.formatPrice(plan.price, plan.currency)}
                                </PlanPrice>
                          ) : (
                            <PlanPrice>
                              <PlanPriceLabel>월</PlanPriceLabel>
                              {licenseService.formatPrice(plan.price, plan.currency)}
                            </PlanPrice>
                          );
                          // 테스트 모드(유료 결제 비활성화) 시 유료 플랜에 이용중지 뱃지
                          if (isDisabledByBeta && plan.planType !== 'free') {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanBadge>이용중지</PlanBadge>
                              </>
                            );
                          }
                          // 1년 탭: 활성화가 1년일 때 동일 플랜 → 기간 확장 (무료 플랜 제외)
                          if (purchaseMode !== 'group' && isOneYearTab && activeIsYear && isCurrentPlan && plan.planType !== 'free') {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanBadge>기간 확장</PlanBadge>
                              </>
                            );
                          }
                          // 1년 탭: 활성화가 1년일 때 상위 플랜 → (업그레이드) + 예상 금액
                          if (purchaseMode !== 'group' && isOneYearTab && activeIsYear && isHigherPlan) {
                            const oneYearUpgrade = calculateOneYearUpgradePrice(plan);
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanBadge>업그레이드</PlanBadge>
                                <PlanUpgradePrice>
                                  예상 {oneYearUpgrade ? oneYearUpgrade.price.toLocaleString() : '-'}원
                                </PlanUpgradePrice>
                              </>
                            );
                          }
                          // 1년 탭: N개월 이용 중일 때 유료 1년 플랜 → (이용권 구입)
                          if (purchaseMode !== 'group' && isOneYearTab && !activeIsYear && plan.planType !== 'free' && !isDisabled) {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanBadge>이용권 구입</PlanBadge>
                              </>
                            );
                          }
                          if (purchaseMode === 'group' && plan.planType !== 'free' && !isDisabled) {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanBadge>그룹 구매</PlanBadge>
                              </>
                            );
                          }

                          // 현재 이용중인 플랜 (N개월 탭): 가격 + [기간 연장] (무료 플랜 제외)
                          if (purchaseMode !== 'group' && isCurrentPlan && plan.planType !== 'free') {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanBadge>기간 연장</PlanBadge>
                              </>
                            );
                          }
                          // 비활성화(다운그레이드 또는 기간 혼용): 가격 + 이용 불가 배지
                          if (isDisabled) {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanUnavailableBadge>이용 불가</PlanUnavailableBadge>
                              </>
                            );
                          }

                          const allLicenses = getAllLicensesArray();
                          const hasPaidLicense = allLicenses.some((l: any) => l.isRefunded !== true);
                          
                          // 유료 플랜이 없으면 정가 + (무료 제외) 이용권 구입 배지
                          if (!hasPaidLicense) {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                {plan.planType !== 'free' && <PlanBadge>이용권 구입</PlanBadge>}
                              </>
                            );
                          }
                          
                          // N개월 탭 상위 플랜: 가격 + [업그레이드] + 예상 가격
                          const upgradeResult = calculateUpgradePriceForPlan(plan);
                          if (upgradeResult !== null) {
                            return (
                              <>
                                {oneYearDiscount}
                                {priceEl}
                                <PlanBadge>업그레이드</PlanBadge>
                                <PlanUpgradePrice>
                                  예상 {upgradeResult.price.toLocaleString()}원
                                </PlanUpgradePrice>
                              </>
                            );
                          }
                          // 그 외 N개월 탭 유료 플랜: 정가 + 이용권 구입 배지
                            return (
                            <>
                              {oneYearDiscount}
                              {priceEl}
                              {plan.planType !== 'free' && <PlanBadge>이용권 구입</PlanBadge>}
                            </>
                          );
                        })()}
                        {plan.planType === 'free' && <PlanBadgeSpacer aria-hidden />}
                      </PlanPriceContainer>
                      <PlanDetails>
                        {groupFeaturesByCategory(plan.planType).map((categoryGroup, categoryIndex) => (
                          <FeatureCategoryGroup key={categoryIndex}>
                            <CategoryLabel>{categoryGroup.category}</CategoryLabel>
                            {categoryGroup.features.map((feature, featureIndex) => (
                              feature ? (
                                <PlanDetailItem key={featureIndex}>{feature}</PlanDetailItem>
                              ) : (
                                <PlanDetailItem key={featureIndex} style={{ minHeight: '1.4em' }}>&nbsp;</PlanDetailItem>
                              )
                            ))}
                          </FeatureCategoryGroup>
                        ))}
                      </PlanDetails>
                    </PlanCard>
                  );
                })}
              </PlanRow>
            </CategorySection>
          )}

          {/* 플랜 비교 표 */}
          <ComparisonSection>
            <ComparisonTitle>플랜 비교</ComparisonTitle>
            <ComparisonTable>
              <TableHead>
                <TableRow>
                  <TableHeader>카테고리</TableHeader>
                  <TableHeader>기능</TableHeader>
                  <TableHeader>프리</TableHeader>
                  <TableHeader>베이직</TableHeader>
                  <TableHeader>스탠다드</TableHeader>
                  <TableHeader>프로</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedData.map((row, index) => (
                  <TableRow key={index}>
                    {row.isFirstInCategory && row.categoryRowspan ? (
                      <CategoryCell rowSpan={row.categoryRowspan} $isLastCategory={row.isLastCategory}>
                        {row.category}
                      </CategoryCell>
                    ) : !row.isFirstInCategory ? null : null}
                    <TableData $isFeature>
                      {row.feature.split('\n').map((line, idx) => (
                        <React.Fragment key={idx}>
                          {line}
                          {idx < row.feature.split('\n').length - 1 && <br />}
                        </React.Fragment>
          ))}
                    </TableData>
                    <TableData>
                      {row.free === '가능' || row.free === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.free === '가능'}>
                          {row.free === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.free
                      )}
                    </TableData>
                    <TableData>
                      {row.basic === '가능' || row.basic === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.basic === '가능'}>
                          {row.basic === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.basic
                      )}
                    </TableData>
                    <TableData>
                      {row.standard === '가능' || row.standard === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.standard === '가능'}>
                          {row.standard === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.standard
                      )}
                    </TableData>
                    <TableData>
                      {row.pro === '가능' || row.pro === '불가' ? (
                        <FeatureStatusBadge $isAvailable={row.pro === '가능'}>
                          {row.pro === '가능' ? (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <circle cx="12" cy="12" r="9" stroke="currentColor" fill="none" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ strokeWidth: 3 }}>
                              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                            </svg>
                          )}
                        </FeatureStatusBadge>
                      ) : (
                        row.pro
                      )}
                    </TableData>
                  </TableRow>
                ))}
              </TableBody>
            </ComparisonTable>
          </ComparisonSection>

        </ContentWrapper>

        {/* 사업자 정보 섹션 */}
        <CompanyInfoSection>
          <CompanyInfoContent>
            <CompanyLogo>
              <img src="/LOGOeducodi_new.svg" alt="에듀코디" />
            </CompanyLogo>
            <CompanyInfoText>
              <div>제이앤조이 대표자 : 정영은</div>
              <div>통신판매업신고번호 : 2024-화성동탄-0316호</div>
              <div>사업자등록번호 : 702-04-02761</div>
              <div>주소 : 경기도 화성시 동탄대로시범길 20. 1415동 2701호</div>
              <PolicyLinks>
                <PolicyLink onClick={handleTermsClick}>[서비스 이용약관]</PolicyLink>
                <PolicyLink onClick={handlePrivacyClick}>[개인정보처리방침]</PolicyLink>
              </PolicyLinks>
              <div>연락처 : 010-7442-0199</div>
              <div>결제 및 상담 : luckytem_mk@naver.com</div>
            </CompanyInfoText>
          </CompanyInfoContent>
        </CompanyInfoSection>
      </Content>

      {/* 쿠폰 등록 모달 */}
      {showCouponModal && (
        <CouponModalOverlay onClick={() => { setShowCouponModal(false); setPendingGroupCouponCode(null); }}>
          <CouponModalContent onClick={(e) => e.stopPropagation()}>
            <CouponModalHeader>
              <CouponModalTitle>쿠폰 코드 등록</CouponModalTitle>
              <CloseButton onClick={() => { setShowCouponModal(false); setPendingGroupCouponCode(null); }}>×</CloseButton>
            </CouponModalHeader>
            <CouponModalBody>
              <CouponInputLabel>쿠폰 코드를 입력하세요</CouponInputLabel>
              <CouponInputWrapper>
                <CouponInputContainer>
                  <CouponInputPart
                    id="coupon-code-prefix"
                    type="text"
                    value={couponCodePrefix}
                    inputMode="text"
                    lang="en"
                    autoComplete="off"
                    onCompositionStart={() => setIsComposingPrefix(true)}
                    onCompositionEnd={() => {
                      setIsComposingPrefix(false);
                    }}
                    onKeyPress={(e) => {
                      // 한글 조합 중일 때는 처리하지 않음
                      if (isComposingPrefix) {
                        return;
                      }
                      
                      // 소문자 키를 대문자로 변환
                      if (e.key.length === 1 && /[a-z]/.test(e.key)) {
                        e.preventDefault();
                        const input = e.currentTarget;
                        const currentValue = input.value;
                        const selectionStart = input.selectionStart || 0;
                        const selectionEnd = input.selectionEnd || 0;
                        const newValue = 
                          currentValue.slice(0, selectionStart) + 
                          e.key.toUpperCase() + 
                          currentValue.slice(selectionEnd);
                        // 영문/숫자만 추출하여 대문자로 변환
                        let filteredValue = newValue.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase();
                        setCouponCodePrefix(filteredValue);
                        
                        // 커서 위치 조정
                        setTimeout(() => {
                          const newCursorPos = Math.min(selectionStart + 1, filteredValue.length);
                          input.setSelectionRange(newCursorPos, newCursorPos);
                        }, 0);
                        
                        if (filteredValue.length === 3) {
                          setTimeout(() => {
                            const suffixInput = document.getElementById('coupon-code-suffix') as HTMLInputElement;
                            if (suffixInput) {
                              suffixInput.focus();
                            }
                          }, 0);
                        }
                      }
                    }}
                    onChange={(e) => {
                      let inputValue = e.target.value;
                      // 길이 제한만 적용
                      inputValue = inputValue.slice(0, 3);
                      
                      // 영문/숫자만 있는 경우 즉각 대문자 변환
                      const onlyAlphanumeric = inputValue.replace(/[^A-Za-z0-9]/g, '');
                      if (onlyAlphanumeric.length === inputValue.length) {
                        const upperValue = inputValue.toUpperCase();
                        setCouponCodePrefix(upperValue);
                        // 3자리 입력 완료 시 뒷부분으로 포커스 이동
                        if (upperValue.length === 3) {
                          setTimeout(() => {
                            const suffixInput = document.getElementById('coupon-code-suffix') as HTMLInputElement;
                            if (suffixInput) {
                              suffixInput.focus();
                            }
                          }, 0);
                        }
                      } else {
                        setCouponCodePrefix(inputValue);
                      }
                    }}
                    onPaste={(e) => {
                      // 붙여넣기 시 대문자 변환
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      let inputValue = pastedText.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 3);
                      setCouponCodePrefix(inputValue);
                      
                      if (inputValue.length === 3) {
                        setTimeout(() => {
                          const suffixInput = document.getElementById('coupon-code-suffix') as HTMLInputElement;
                          if (suffixInput) {
                            suffixInput.focus();
                          }
                        }, 0);
                      }
                    }}
                    onFocus={(e) => {
                      // 포커스 시 영문 입력 모드 유도
                      const input = e.currentTarget;
                      input.setAttribute('inputmode', 'text');
                      input.setAttribute('lang', 'en');
                    }}
                    placeholder="000"
                    disabled={isProcessingCoupon}
                    maxLength={3}
                    autoFocus
                    $isPrefix={true}
                  />
                <CouponHyphen>-</CouponHyphen>
                  <CouponInputPart
                    id="coupon-code-suffix"
                    type="text"
                    value={couponCodeSuffix}
                    inputMode="text"
                    lang="en"
                    autoComplete="off"
                    onCompositionStart={() => setIsComposingSuffix(true)}
                    onCompositionEnd={() => {
                      setIsComposingSuffix(false);
                    }}
                    onKeyDown={(e) => {
                      // 뒷부분이 비어있을 때 백스페이스 시 앞부분으로 포커스 이동
                      if (e.key === 'Backspace' && couponCodeSuffix.length === 0) {
                        e.preventDefault();
                        const prefixInput = document.getElementById('coupon-code-prefix') as HTMLInputElement;
                        if (prefixInput) {
                          prefixInput.focus();
                        }
                      }
                    }}
                    onKeyPress={(e) => {
                      // 한글 조합 중일 때는 처리하지 않음
                      if (isComposingSuffix) {
                        return;
                      }
                      
                      // 소문자 키를 대문자로 변환
                      if (e.key.length === 1 && /[a-z]/.test(e.key)) {
                        e.preventDefault();
                        const input = e.currentTarget;
                        const currentValue = input.value;
                        const selectionStart = input.selectionStart || 0;
                        const selectionEnd = input.selectionEnd || 0;
                        const newValue = 
                          currentValue.slice(0, selectionStart) + 
                          e.key.toUpperCase() + 
                          currentValue.slice(selectionEnd);
                        // 영문/숫자만 추출하여 대문자로 변환
                        const maxSuffix = couponCodePrefix.toUpperCase() === 'GRP' ? 9 : 7;
                        let filteredValue = newValue.replace(/[^A-Za-z0-9]/g, '').slice(0, maxSuffix).toUpperCase();
                        setCouponCodeSuffix(filteredValue);
                        
                        // 커서 위치 조정
                        setTimeout(() => {
                          const newCursorPos = Math.min(selectionStart + 1, filteredValue.length);
                          input.setSelectionRange(newCursorPos, newCursorPos);
                        }, 0);
                      }
                    }}
                    onChange={(e) => {
                      let inputValue = e.target.value;
                      const maxSuffix = couponCodePrefix.toUpperCase() === 'GRP' ? 9 : 7;
                      inputValue = inputValue.slice(0, maxSuffix);
                      
                      // 영문/숫자만 있는 경우 자동 대문자 변환
                      const onlyAlphanumeric = inputValue.replace(/[^A-Za-z0-9]/g, '');
                      if (onlyAlphanumeric.length === inputValue.length) {
                        setCouponCodeSuffix(inputValue.toUpperCase());
                      } else {
                        setCouponCodeSuffix(inputValue);
                      }
                    }}
                    onPaste={(e) => {
                      // 붙여넣기 시 대문자 변환
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      const maxSuffix = couponCodePrefix.toUpperCase() === 'GRP' ? 9 : 7;
                      let inputValue = pastedText.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, maxSuffix);
                      setCouponCodeSuffix(inputValue);
                    }}
                    onFocus={(e) => {
                      // 포커스 시 영문 입력 모드 유도
                      const input = e.currentTarget;
                      input.setAttribute('inputmode', 'text');
                      input.setAttribute('lang', 'en');
                    }}
                    placeholder={couponCodePrefix.toUpperCase() === 'GRP' ? '000000000' : '0000000'}
                    disabled={isProcessingCoupon}
                    maxLength={couponCodePrefix.toUpperCase() === 'GRP' ? 9 : 7}
                    $isPrefix={false}
                  />
                </CouponInputContainer>
                {(() => {
                  const hasKorean = /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(couponCodePrefix) || /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(couponCodeSuffix);
                  return hasKorean ? (
                    <CouponInputMessage>
                      영문 대문자와 숫자를 입력해주세요
                    </CouponInputMessage>
                  ) : null;
                })()}
              </CouponInputWrapper>
              <CouponButtonGroup>
                <CouponCancelButton onClick={() => {
                  setShowCouponModal(false);
                  setPendingGroupCouponCode(null);
                  setCouponCodePrefix('');
                  setCouponCodeSuffix('');
                }} disabled={isProcessingCoupon}>
                  취소
                </CouponCancelButton>
                <CouponSubmitButton onClick={handleCouponSubmit} disabled={isProcessingCoupon || !couponCodePrefix.trim() || !couponCodeSuffix.trim() || couponCodePrefix.length !== 3 || couponCodeSuffix.length !== (couponCodePrefix.toUpperCase() === 'GRP' ? 9 : 7)}>
                  {isProcessingCoupon ? '처리 중...' : '등록'}
                </CouponSubmitButton>
              </CouponButtonGroup>
            </CouponModalBody>
          </CouponModalContent>
        </CouponModalOverlay>
      )}

      {pendingGroupCouponCode && (
        <RefundOrderModalOverlay onClick={() => setPendingGroupCouponCode(null)}>
          <RefundOrderModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
            <RefundOrderModalHeader>
              <RefundOrderModalTitle>그룹 이용권 적용 안내</RefundOrderModalTitle>
              <CloseButton onClick={() => setPendingGroupCouponCode(null)}>×</CloseButton>
            </RefundOrderModalHeader>
            <RefundOrderModalBody>
              <RefundOrderMessage style={{ textAlign: 'left', fontSize: 14, lineHeight: 1.7 }}>
                기존 이용권이 있습니다. 그룹 이용권을 적용하시겠습니까?
                <br /><br />
                <strong>그룹 이용권 적용 시:</strong>
                <br />
                · 기존 이용권은 <strong>일시정지</strong>되고 그룹 이용권이 <strong>우선 적용</strong>됩니다.
                <br />
                · 그룹 이용권은 <strong>적용 취소나 되돌리기가 불가</strong>하며, 환불되지 않는 이상 기존 이용권보다 우선 적용됩니다.
                <br />
                · 그룹 이용권이 <strong>만료된 후에만</strong> 기존 이용권이 이어서 적용됩니다.
                <br /><br />
                <em>※ 그룹 이용권 적용 중에도 상위 플랜 업그레이드나 기간 연장 등 추가 이용권 구매를 통한 업그레이드는 가능합니다.</em>
              </RefundOrderMessage>
              <RefundOrderButtonGroup style={{ gap: 12, marginTop: 12 }}>
                <RefundModalCancelButton onClick={() => setPendingGroupCouponCode(null)}>닫기</RefundModalCancelButton>
                <RefundOrderConfirmButton
                  onClick={() => pendingGroupCouponCode && submitCouponCode(pendingGroupCouponCode, true)}
                  disabled={isProcessingCoupon}
                >
                  {isProcessingCoupon ? '처리 중...' : '적용'}
                </RefundOrderConfirmButton>
              </RefundOrderButtonGroup>
            </RefundOrderModalBody>
          </RefundOrderModalContent>
        </RefundOrderModalOverlay>
      )}

      {/* N개월/1년 이용권 구입 모달 */}
      {nMonthModalPlan && (
        <NMonthPlanModal
          isOpen={!!nMonthModalPlan}
          plan={nMonthModalPlan}
          mode={(() => {
            if (nMonthModalPlan.duration === 12 && oneYearModalIntent) return 'add';
            const currentActive = getCurrentActiveLicense();
            const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
            const isHigher = !!currentActive.planType && currentActive.planType !== 'free' && planOrder.indexOf(nMonthModalPlan.planType) > planOrder.indexOf(currentActive.planType);
            return isHigher ? 'upgrade' : 'add';
          })() as NMonthModalMode}
          upgradePrice={(() => {
            if (nMonthModalPlan.duration === 12 && oneYearModalIntent) return null;
            const currentActive = getCurrentActiveLicense();
            const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
            const isHigher = !!currentActive.planType && currentActive.planType !== 'free' && planOrder.indexOf(nMonthModalPlan.planType) > planOrder.indexOf(currentActive.planType);
            const result = isHigher ? calculateUpgradePriceForPlan(nMonthModalPlan) : null;
            return result?.price ?? null;
          })()}
          upgradeCalculationLines={(() => {
            if (nMonthModalPlan.duration === 12 && oneYearModalIntent) return [];
            const currentActive = getCurrentActiveLicense();
            const planOrder: UserType[] = ['free', 'basic', 'standard', 'pro'];
            const isHigher = !!currentActive.planType && currentActive.planType !== 'free' && planOrder.indexOf(nMonthModalPlan.planType) > planOrder.indexOf(currentActive.planType);
            const result = isHigher ? calculateUpgradePriceForPlan(nMonthModalPlan) : null;
            return result?.calculationLines ?? [];
          })()}
          planTypeName={getPlanTypeName(nMonthModalPlan.planType)}
          onClose={() => { setNMonthModalPlan(null); setOneYearModalIntent(null); }}
          onConfirmAdd={(months) => handleNMonthConfirmAdd(nMonthModalPlan, months)}
          onConfirmUpgrade={() => handleNMonthConfirmUpgrade(nMonthModalPlan)}
          purchaseMode={purchaseMode}
          onConfirmGroupPurchase={(payload) => handleGroupPurchaseConfirm(nMonthModalPlan, payload)}
          oneYearPrice={nMonthModalPlan.duration === 12 && oneYearModalIntent ? (() => {
            if (oneYearModalIntent === 'extension') {
              const ext = calculateOneYearExtensionPrice(nMonthModalPlan);
              return ext && !ext.isRestricted ? ext.finalAmount : undefined;
            }
            if (oneYearModalIntent === 'upgrade') {
              const result = calculateOneYearUpgradePrice(nMonthModalPlan);
              return result?.price;
            }
            return nMonthModalPlan.price;
          })() : undefined}
          oneYearIntent={oneYearModalIntent ?? undefined}
          onConfirmOneYear={nMonthModalPlan.duration === 12 && oneYearModalIntent ? () => {
            const plan = nMonthModalPlan;
            setNMonthModalPlan(null);
            setOneYearModalIntent(null);
            if (oneYearModalIntent === 'extension') handleYearExtensionConfirm(plan);
            else if (oneYearModalIntent === 'upgrade') handleYearUpgradeConfirm(plan);
            else handlePurchase(plan);
          } : undefined}
        />
      )}

      {/* 1년 기간 확장 모달: 현재 시점부터 1년 이용기간 재설정, 확장된 날 수 × (할인가/365) 차액만 결제 */}
      {yearExtensionModalPlan && yearExtensionModalPlan.duration === 12 && (() => {
        const ext = calculateOneYearExtensionPrice(yearExtensionModalPlan);
        const plan = yearExtensionModalPlan;
        return (
          <YearExtensionModalOverlay onClick={() => setYearExtensionModalPlan(null)}>
            <YearExtensionModalContent onClick={(e) => e.stopPropagation()}>
              <YearExtensionModalHeader>
                <YearExtensionModalTitle>{getPlanTypeName(plan.planType)} · 1년 기간 확장</YearExtensionModalTitle>
                <CloseButton type="button" onClick={() => setYearExtensionModalPlan(null)} aria-label="닫기">×</CloseButton>
              </YearExtensionModalHeader>
              <YearExtensionModalBody>
                {!ext ? (
                  <YearExtensionRestrictMessage>이용권 정보를 확인할 수 없습니다.</YearExtensionRestrictMessage>
                ) : ext.isRestricted ? (
                  <YearExtensionRestrictMessage>
                    현재 이용권의 이용기간이 1년 이상입니다. 1년 기간 확장은 활성화 플랜의 이용기간이 1년 미만인 경우 구입할 수 있습니다.
                  </YearExtensionRestrictMessage>
                ) : (
                  <>
                    <YearExtensionNotice>
                      기간 확장 시 이용기간이 <strong>현재 시점부터 1년</strong>으로 재설정됩니다. 확장된 날 수만큼 할인가 기준 일금액으로 차액만 결제합니다.
                    </YearExtensionNotice>
                    <YearExtensionSectionLabel>확장 후 이용기간</YearExtensionSectionLabel>
                    <YearExtensionFixedMonths>현재 시점 ~ 1년</YearExtensionFixedMonths>
                    <YearExtensionRowLabel>1년 플랜 할인가</YearExtensionRowLabel>
                    <YearExtensionDiscountPrice>{licenseService.formatPrice(plan.price, plan.currency)}</YearExtensionDiscountPrice>
                    <YearExtensionRowLabel>확장된 날 수</YearExtensionRowLabel>
                    <YearExtensionDiscountText>{ext.extendedDays}일</YearExtensionDiscountText>
                    <YearExtensionRowLabel>일금액 (할인가 ÷ 365일)</YearExtensionRowLabel>
                    <YearExtensionDiscountText>{Math.round(ext.dailyRate).toLocaleString()}원/일</YearExtensionDiscountText>
                    <YearExtensionFinalLabel>추가 결제 금액 (차액)</YearExtensionFinalLabel>
                    <YearExtensionFinalAmount>{ext.finalAmount.toLocaleString()}원</YearExtensionFinalAmount>
                  </>
                )}
                {ext && !ext.isRestricted && (
                  <LicenseAgreementBlock>
                    <LicenseAgreementLabel>
                      <input type="checkbox" checked={yearExtAgree1} onChange={(e) => setYearExtAgree1(e.target.checked)} id="yearext-agree-activation" />
                      <span>이용권 구매 즉시 활성화됩니다.</span>
                    </LicenseAgreementLabel>
                    <LicenseAgreementLabel>
                      <input type="checkbox" checked={yearExtAgree2} onChange={(e) => setYearExtAgree2(e.target.checked)} id="yearext-agree-refund" />
                      <span>환불은 구매 후 7일 이내 가능하며 환불 시 취소 수수료 10% 및 이용권의 1일 금액 × 사용일수(구매일 포함) 만큼 제외한 금액이 환불됩니다.</span>
                    </LicenseAgreementLabel>
                  </LicenseAgreementBlock>
                )}
              </YearExtensionModalBody>
              <YearExtensionModalFooter>
                <YearExtensionCancelButton type="button" onClick={() => setYearExtensionModalPlan(null)}>취소</YearExtensionCancelButton>
                {ext && !ext.isRestricted && (
                  <YearExtensionSubmitButton type="button" onClick={() => handleYearExtensionConfirm()} disabled={!yearExtAgree1 || !yearExtAgree2} $disabled={!yearExtAgree1 || !yearExtAgree2}>결제하기</YearExtensionSubmitButton>
                )}
              </YearExtensionModalFooter>
            </YearExtensionModalContent>
          </YearExtensionModalOverlay>
        );
      })()}

      {/* 1년 업그레이드 모달 (상위 플랜 1년 - 남은 기간 현재 플랜 가치) */}
      {yearUpgradeModalPlan && (() => {
        const result = calculateOneYearUpgradePrice(yearUpgradeModalPlan);
        return result && result.price > 0 ? (
          <YearUpgradeModalOverlay onClick={() => setYearUpgradeModalPlan(null)}>
            <YearUpgradeModalContent onClick={(e) => e.stopPropagation()}>
              <YearUpgradeModalHeader>
                <YearUpgradeModalTitle>{getPlanTypeName(yearUpgradeModalPlan.planType)} · 1년 기간 연장 + 업그레이드</YearUpgradeModalTitle>
                <CloseButton type="button" onClick={() => setYearUpgradeModalPlan(null)} aria-label="닫기">×</CloseButton>
              </YearUpgradeModalHeader>
              <YearUpgradeModalBody>
                <YearUpgradeCostLabel>업그레이드 비용</YearUpgradeCostLabel>
                <YearUpgradeCostAmount>{result.price.toLocaleString()}원</YearUpgradeCostAmount>
                {result.calculationLines.length > 0 && (
                  <YearUpgradeCalculationBlock>
                    {result.calculationLines.map((line, i) => (
                      <YearUpgradeCalculationLine key={i}>{line}</YearUpgradeCalculationLine>
                    ))}
                  </YearUpgradeCalculationBlock>
                )}
                <LicenseAgreementBlock>
                  <LicenseAgreementLabel>
                    <input type="checkbox" checked={yearUpgradeAgree1} onChange={(e) => setYearUpgradeAgree1(e.target.checked)} id="yearup-agree-activation" />
                    <span>이용권 구매 즉시 활성화됩니다.</span>
                  </LicenseAgreementLabel>
                  <LicenseAgreementLabel>
                    <input type="checkbox" checked={yearUpgradeAgree2} onChange={(e) => setYearUpgradeAgree2(e.target.checked)} id="yearup-agree-refund" />
                    <span>환불은 구매 후 7일 이내 가능하며 환불 시 취소 수수료 10% 및 이용권의 1일 금액 × 사용일수(구매일 포함) 만큼 제외한 금액이 환불됩니다.</span>
                  </LicenseAgreementLabel>
                </LicenseAgreementBlock>
              </YearUpgradeModalBody>
              <YearUpgradeModalFooter>
                <YearUpgradeCancelButton type="button" onClick={() => setYearUpgradeModalPlan(null)}>취소</YearUpgradeCancelButton>
                <YearUpgradeSubmitButton type="button" onClick={() => handleYearUpgradeConfirm()} disabled={!yearUpgradeAgree1 || !yearUpgradeAgree2} $disabled={!yearUpgradeAgree1 || !yearUpgradeAgree2}>결제하기</YearUpgradeSubmitButton>
              </YearUpgradeModalFooter>
            </YearUpgradeModalContent>
          </YearUpgradeModalOverlay>
        ) : null;
      })()}

      {/* 테스트 비밀번호 모달 */}
      {showTestPasswordModal && (
        <TestPasswordModalOverlay onClick={handleTestPasswordCancel}>
          <TestPasswordModalContent onClick={(e) => e.stopPropagation()}>
            <TestPasswordModalHeader>
              <TestPasswordModalTitle>테스트 모드</TestPasswordModalTitle>
              <CloseButton onClick={handleTestPasswordCancel}>×</CloseButton>
            </TestPasswordModalHeader>
            <TestPasswordModalBody>
              <TestPasswordLabel>관리자 계정 비밀번호를 입력하세요</TestPasswordLabel>
              <TestPasswordInput
                type="password"
                value={testPassword}
                onChange={(e) => setTestPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isProcessingTestGroup) {
                    e.preventDefault();
                    handleTestPasswordConfirm();
                  }
                }}
                placeholder="비밀번호 입력"
                autoFocus
              />
              <TestPasswordButtonGroup>
                <TestPasswordCancelButton onClick={handleTestPasswordCancel}>
                  취소
                </TestPasswordCancelButton>
                <TestPasswordConfirmButton 
                  onClick={handleTestPasswordConfirm}
                  disabled={!testPassword.trim() || isProcessingTestGroup}
                >
                  {isProcessingTestGroup ? '처리 중...' : '확인'}
                </TestPasswordConfirmButton>
              </TestPasswordButtonGroup>
            </TestPasswordModalBody>
          </TestPasswordModalContent>
        </TestPasswordModalOverlay>
      )}
      {showToast && (
        <Toast
          message={toastMessage}
          messageDetail={toastMessageDetail || undefined}
          onClose={() => { setShowToast(false); setToastConfirmOnly(false); setToastMessageDetail(''); }}
          duration={2000}
          confirmOnly={toastConfirmOnly}
          confirmLabel="확인"
        />
      )}

      {/* 환불 순서 안내 모달 */}
      {showRefundOrderModal && (
        <RefundOrderModalOverlay onClick={() => setShowRefundOrderModal(false)}>
          <RefundOrderModalContent onClick={(e) => e.stopPropagation()}>
            <RefundOrderModalHeader>
              <RefundOrderModalTitle>환불 순서 안내</RefundOrderModalTitle>
              <CloseButton onClick={() => setShowRefundOrderModal(false)}>×</CloseButton>
            </RefundOrderModalHeader>
            <RefundOrderModalBody>
              <RefundOrderMessage>
                최근 플랜 업그레이드 환불 진행을 먼저 해주세요.<br />
                <br />
                플랜 업그레이드 순서대로 환불이 진행되어야<br />
                이용권 상태가 올바르게 유지됩니다.
              </RefundOrderMessage>
              <RefundOrderButtonGroup>
                <RefundOrderConfirmButton onClick={() => setShowRefundOrderModal(false)}>
                  확인
                </RefundOrderConfirmButton>
              </RefundOrderButtonGroup>
            </RefundOrderModalBody>
          </RefundOrderModalContent>
        </RefundOrderModalOverlay>
      )}

      {/* 환불 안내 모달 (활성 이용권 환불 요청 시) */}
      {refundModalLicense && (() => {
        const lic = refundModalLicense as any;
        const paidAmount = lic.amount || 0;
        const periodStart = toDateSafe(lic.periodStart ?? lic.purchasedAt) ?? new Date();
        const duration = lic.duration ?? 1;
        const r = getRefundAmountKrwForActivated(paidAmount, periodStart, duration, { isExtension: lic.isExtension === true, periodEndDate: lic.expiresAt });
        return (
          <RefundOrderModalOverlay onClick={() => setRefundModalLicense(null)}>
            <RefundOrderModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
              <RefundOrderModalHeader>
                <RefundOrderModalTitle>환불 안내</RefundOrderModalTitle>
                <CloseButton onClick={() => setRefundModalLicense(null)}>×</CloseButton>
              </RefundOrderModalHeader>
              <RefundOrderModalBody>
                <RefundOrderMessage style={{ textAlign: 'left' }}>
                  <strong>환불 금액</strong>: {r.refundKrw.toLocaleString()}원
                </RefundOrderMessage>
                <RefundOrderMessage style={{ textAlign: 'left', fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                  {paidAmount.toLocaleString()}원 - 수수료(10%) {r.feeKrw.toLocaleString()}원{r.extensionFuturePeriod ? '' : ` - 사용${r.daysUsed}일 차감 ${r.usedDaysDeductionKrw.toLocaleString()}원`} = {r.refundKrw.toLocaleString()}원
                </RefundOrderMessage>
                <RefundOrderMessage style={{ textAlign: 'left', fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
                  <span>· 취소·운영 수수료: {r.feeKrw.toLocaleString()}원</span>
                  {!r.extensionFuturePeriod && (
                    <span>· 사용일수 차감: 일금액 {r.dailyRateKrw.toLocaleString()}원 × {r.daysUsed}일 = {r.usedDaysDeductionKrw.toLocaleString()}원</span>
                  )}
                </RefundOrderMessage>
                <RefundOrderMessage style={{ textAlign: 'left', fontSize: 14 }}>
                  · 환불은 처리 후 2~3일 내에 결제 수단으로 입금될 수 있습니다.<br />
                  · 구매일로부터 7일 이내에만 환불 가능합니다.<br />
                  · 환불 시 해당 이용권은 즉시 해지됩니다.
                </RefundOrderMessage>
                <RefundOrderButtonGroup style={{ gap: 12, marginTop: 8 }}>
                  <RefundModalCancelButton onClick={() => setRefundModalLicense(null)}>취소</RefundModalCancelButton>
                  <RefundOrderConfirmButton
                    onClick={() => performRefundRequest(refundModalLicense)}
                    disabled={isProcessingRefund[refundModalLicense.paymentId]}
                  >
                    {isProcessingRefund[refundModalLicense.paymentId] ? '처리 중...' : '환불하기'}
                  </RefundOrderConfirmButton>
                </RefundOrderButtonGroup>
              </RefundOrderModalBody>
            </RefundOrderModalContent>
          </RefundOrderModalOverlay>
        );
      })()}

      <LicenseProofDocumentModal
        open={licenseProofModal != null}
        onClose={() => setLicenseProofModal(null)}
        model={
          licenseProofModal
            ? licenseProofModal.source === 'license'
              ? buildLicenseProofDisplayModel(licenseProofModal.license, effectiveUser)
              : buildGroupBatchProofDisplayModel(licenseProofModal.batch, effectiveUser)
            : null
        }
        portonePaymentId={(() => {
          if (!licenseProofModal) return null;
          if (licenseProofModal.source === 'license') {
            const id = String(licenseProofModal.license?.paymentId || '');
            if (!id || id.startsWith('COUPON-') || id.startsWith('TEST_')) return null;
            return id;
          }
          const b = licenseProofModal.batch;
          if (b.adminIssued === true || b.paymentMode === 'test') return null;
          return String(b.paymentId || '') || null;
        })()}
      />

      {/* 그룹 이용권 환불 안내 모달 (환불 전 확인 + 구체적 숫자) */}
      {refundModalGroupBatch && groupRefundQuote && (
        <RefundOrderModalOverlay onClick={() => { setRefundModalGroupBatch(null); setGroupRefundQuote(null); }}>
          <RefundOrderModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <RefundOrderModalHeader>
              <RefundOrderModalTitle>환불 안내</RefundOrderModalTitle>
              <CloseButton onClick={() => { setRefundModalGroupBatch(null); setGroupRefundQuote(null); }}>×</CloseButton>
            </RefundOrderModalHeader>
            <RefundOrderModalBody>
              <RefundOrderMessage style={{ textAlign: 'left' }}>
                <strong>환불 금액</strong>: {groupRefundQuote.refundKrw.toLocaleString()}원
              </RefundOrderMessage>
              <RefundOrderMessage style={{ textAlign: 'left', fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
                미사용 {groupRefundQuote.unusedCount}명 × {groupRefundQuote.unitPriceKrw.toLocaleString()}원 = {groupRefundQuote.unusedRefundKrw.toLocaleString()}원
                {groupRefundQuote.activatedRefundedCount > 0 && (
                  <> + 활성화 {groupRefundQuote.activatedRefundedCount}명 부분환불 {groupRefundQuote.activatedRefundKrw.toLocaleString()}원</>
                )}
                {' = '}{groupRefundQuote.refundKrw.toLocaleString()}원
              </RefundOrderMessage>
              <RefundOrderMessage style={{ textAlign: 'left', fontSize: 12, color: 'var(--color-text-muted)', marginTop: 8 }}>
                · 미사용: 전액 환불 / 활성화: 수수료 10%·사용일수 차감 후 환불
              </RefundOrderMessage>
              <RefundOrderMessage style={{ textAlign: 'left', fontSize: 14 }}>
                · 환불은 처리 후 2~3일 내에 결제 수단으로 입금될 수 있습니다.<br />
                · 구매일로부터 7일 이내에만 환불 가능합니다.<br />
                · 환불 시 해당 그룹 이용권은 즉시 해지됩니다.
              </RefundOrderMessage>
              <RefundOrderButtonGroup style={{ gap: 12, marginTop: 8 }}>
                <RefundModalCancelButton onClick={() => { setRefundModalGroupBatch(null); setGroupRefundQuote(null); }}>닫기</RefundModalCancelButton>
                <RefundOrderConfirmButton
                  onClick={performGroupCouponRefund}
                  disabled={isProcessingGroupRefund[refundModalGroupBatch.batchId]}
                >
                  {isProcessingGroupRefund[refundModalGroupBatch.batchId] ? '처리 중...' : '환불하기'}
                </RefundOrderConfirmButton>
              </RefundOrderButtonGroup>
            </RefundOrderModalBody>
          </RefundOrderModalContent>
        </RefundOrderModalOverlay>
      )}

    </Container>
  );
};

export default LicensePage;

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-panel);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Content = styled.div`
  position: absolute;
  top: 44px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  ${hideScrollbar}
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
  width: 100%;
  flex: 1;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  padding: 40px;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid #fcc;
`;

const CurrentLicenseInfoCard = styled.div`
  background: var(--color-surface);
  border: 2px solid var(--color-border);
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  text-align: center;
  width: 100%;
`;

const Divider = styled.div`
  width: 1px;
  height: 40px;
  background: var(--color-border);
  
  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
  }
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary);
`;

const PlanNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PlanNameLarge = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-accent-strong);
  margin-bottom: 0;
  
  /* 다크 모드에서 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

/** 쿠폰/그룹 이용권 표시용 뱃지 (LicenseDurationBadge와 동일 높이) */
const CouponBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: var(--color-panel);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  box-sizing: border-box;
`;

/** 관리자 발급 표시용 뱃지 */
const AdminIssuedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  background: var(--color-surface-highlight, #f0f7ff);
  color: var(--color-accent-strong, #2563eb);
  border: 1px solid var(--color-accent, #3b82f6);
  box-sizing: border-box;
`;

const LicensePeriodText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-accent-strong);
  margin-top: 2px;
  
  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const CurrentPlanPeriodCenter = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const CurrentPlanPeriodWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const CurrentPlanPeriodRange = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-accent-strong);
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const CurrentPlanPeriodLabel = styled.span`
  display: block;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-strong);
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const InfoValue = styled.div<{ $isUnlimited?: boolean }>`
  font-size: ${props => props.$isUnlimited ? '24px' : '14px'};
  font-weight: ${props => props.$isUnlimited ? '700' : '600'};
  color: ${props => props.$isUnlimited ? 'var(--color-accent-strong)' : 'var(--color-text-primary)'};
  
  /* 다크 모드에서 큰 텍스트(24px)는 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$isUnlimited ? 'white' : 'var(--color-text-primary)'};
  }
`;

const CouponButtonContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
`;

const CouponButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.8);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 24px;
  flex-shrink: 0;
`;

const PurchaseModeToggleContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  justify-content: center;
`;

const PurchaseModeToggleButton = styled.button<{ $isActive: boolean }>`
  border: 1px solid ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$isActive ? '#fff' : 'var(--color-text-primary)'};
  border-radius: 999px;
  padding: 8px 14px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`;

const LicenseInfoMessage = styled.div`
  text-align: center;
  margin-top: 16px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const RefundInfoSection = styled.div`
  margin-top: 48px;
  margin-bottom: 24px;
`;

const RefundInfoTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 24px 0;
  text-align: center;
`;

const RefundInfoMessage = styled.div`
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  padding: 8px 16px;
  background: var(--color-panel);
  border-radius: 8px;
`;

const LicenseHistoryCard = styled.div`
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  :root:not([data-theme='light']) & {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const LicenseHistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
`;

const LicenseHistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0;
`;

const DetailHistoryButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-accent-strong);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  text-decoration: underline;
  
  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: white;
  }
  
  &:hover {
    opacity: 0.8;
    background: var(--color-panel);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const LicenseHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LicenseTotalAmountText = styled.div`
  text-align: center;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border);
  font-size: 18px;
  font-weight: 700;
  color: var(--color-accent-strong);
  
  /* 다크 모드: 흰색 */
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const PurchaseDateBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-secondary);
  white-space: nowrap;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const LicenseHistoryBlock = styled.div<{ $isRefunded?: boolean; $isExpired?: boolean; $isActivated?: boolean }>`
  position: relative;
  background: ${props => props.$isRefunded || props.$isExpired ? 'var(--color-panel-disabled, #f5f5f5)' : 'var(--color-panel)'};
  border: 2px solid ${props => (props.$isActivated && !props.$isRefunded && !props.$isExpired) ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 8px;
  padding: 16px 20px 16px 28px;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 20px;
  opacity: ${props => props.$isRefunded || props.$isExpired ? 0.6 : 1};
  transition: opacity 0.2s ease;
  z-index: ${props => (props.$isActivated && !props.$isRefunded && !props.$isExpired) ? 1 : 0};
  box-shadow: ${props => (props.$isActivated && !props.$isRefunded && !props.$isExpired) ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none'};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 16px 16px 16px 24px;
  }
  
  :root:not([data-theme='light']) & {
    background: ${props => (props.$isRefunded || props.$isExpired) ? 'var(--color-panel-disabled, #2a2a2a)' : 'var(--color-panel)'};
    border-color: ${props => (props.$isActivated && !props.$isRefunded && !props.$isExpired) ? 'white' : 'var(--color-border)'};
    box-shadow: ${props => (props.$isActivated && !props.$isRefunded && !props.$isExpired) ? '0 4px 12px rgba(0, 0, 0, 0.4)' : 'none'};
  }
`;

const LicenseHistoryLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const LicenseHistoryRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  min-width: 0;
`;

const LicenseHistoryMiddle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  min-width: 0;
`;

const LicenseHistoryProof = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  min-width: 0;
`;

const ProofButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 240px;
  margin-top: 4px;
`;

const ProofDocButton = styled.button`
  width: 100%;
  padding: 8px 16px;
  border: 1px solid var(--color-border);
  background: white;
  color: var(--color-text-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  height: 36px;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    background: #f5f5f5;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    background: #f5f5f5;
    color: var(--color-text-secondary);
    border-color: var(--color-border);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  :root:not([data-theme='light']) & {
    background: #2a2a2a;
    border-color: #555;
    color: var(--color-text-primary);

    &:hover:not(:disabled) {
      background: #333;
    }

    &:disabled {
      background: #2a2a2a;
      border-color: #555;
    }
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ProofSmallPrint = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary);
  text-align: center;
  line-height: 1.4;
  max-width: 220px;
`;

const LicenseBadgeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
  min-height: 36px;
`;

const RefundButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
`;

const LicenseHistoryDivider = styled.div`
  width: 1px;
  height: auto;
  align-self: stretch;
  background: var(--color-border);
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    height: 1px;
  }
`;

const LicenseHistoryLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-bottom: 2px;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
`;

const EmailStatusIndicator = styled.div<{ $success: boolean }>`
  width: 22px;
  height: 22px;
  min-width: 22px;
  min-height: 22px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid ${props => props.$success ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  background: ${props => props.$success ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  color: ${props => props.$success ? '#ffffff' : 'var(--color-text-gray)'};
  font-size: 14px;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  
  /* 다크 모드: AssignmentBlock O/X 스타일과 동일 */
  :root:not([data-theme='light']) & {
    border-color: ${props => props.$success ? '#ffffff' : 'var(--color-border)'};
    background: ${props => props.$success ? '#ffffff' : 'var(--color-surface)'};
    color: ${props => props.$success ? '#333333' : 'var(--color-text-gray)'};
  }
`;

const LicensePeriodDateText = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 2px;
`;

const LicenseDurationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background: white;
  color: var(--color-text-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid var(--color-border);
  min-width: 120px;
  height: 36px;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  :root:not([data-theme='light']) & {
    background: #2a2a2a;
    border-color: #555;
    color: var(--color-text-primary);
  }
`;

const LicenseRefundPeriodText = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 2px;
`;

const LicensePaymentInfoText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
  line-height: 1.4;
`;

/** 활성 X/Y 표시용 눈에 띄는 네모 블럭 */
const ActivatedCountBlock = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding: 8px 16px;
  margin: 8px auto;
  background: white;
  color: var(--color-text-primary);
  font-size: 16px;
  font-weight: 700;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  align-self: center;

  :root:not([data-theme='light']) & {
    background: var(--color-panel);
  }
`;

const LicenseRefundAmountText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
  line-height: 1.4;
`;

const PendingActivationNoticeWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
  align-self: flex-start;
  width: 100%;
`;

const PendingActivationNotice = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const RefundSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  align-items: flex-start;
  min-width: 200px;
  
  @media (max-width: 768px) {
    width: 100%;
    align-items: flex-start;
  }
`;

const RefundEligibleText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-accent-strong);
  
  :root:not([data-theme='light']) & {
    color: #4CAF50;
  }
`;

const RefundIneligibleText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  opacity: 0.7;
`;

const RefundRequestButton = styled.button<{ $isNotEligible?: boolean }>`
  padding: 8px 16px;
  border: 1px solid var(--color-border);
  background: ${props => props.$isNotEligible ? '#f0f0f0' : 'white'};
  color: ${props => props.$isNotEligible ? '#999' : 'var(--color-text-primary)'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.$isNotEligible ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  min-width: 120px;
  height: 36px;
  box-sizing: border-box;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    background: ${props => props.$isNotEligible ? '#f0f0f0' : '#f5f5f5'};
    transform: ${props => props.$isNotEligible ? 'none' : 'translateY(-1px)'};
    box-shadow: ${props => props.$isNotEligible ? '0 2px 4px rgba(0, 0, 0, 0.1)' : '0 4px 8px rgba(0, 0, 0, 0.15)'};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    background: #f5f5f5;
    color: var(--color-text-secondary);
    border-color: var(--color-border);
  }

  :root:not([data-theme='light']) & {
    background: #2a2a2a;
    border-color: #555;
    color: var(--color-text-primary);
    
    &:hover:not(:disabled) {
      background: #333;
    }
    
    &:disabled {
      background: #2a2a2a;
      border-color: #555;
    }
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 16px 24px;
  border: none;
  color: ${props => props.$isActive ? 'white' : 'var(--color-text-secondary)'};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 0;

  /* 선택 버튼 배경색 */
  background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-panel)'};

  /* 비선택 버튼 배경색 - 라이트 모드: 배경보다 어둡게 */
  :root[data-theme='light'] & {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : '#F0F0F0'};
  }

  /* 비선택 버튼 배경색 - 다크 모드: 배경보다 밝게 */
  :root:not([data-theme='light']) & {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-surface)'};
  }

  &:first-child {
    border-top-left-radius: 8px;
  }

  &:last-child {
    border-top-right-radius: 8px;
  }

  &:hover {
    background: ${props => props.$isActive ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: -2px;
  }
`;

const CategorySection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PlanDurationNotice = styled.div`
  text-align: center;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: var(--color-panel);
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
`;

const BetaTestNotice = styled.div`
  margin-bottom: 16px;
  padding: 12px 16px;
  background: #fff8e6;
  border: 1px solid #e6d9b8;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #8b6914;
  :root:not([data-theme='light']) & {
    background: rgba(255, 236, 179, 0.15);
    border-color: rgba(230, 217, 184, 0.6);
    color: #e6d9a0;
  }
`;

const PlanRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div<{ $isCurrent: boolean; $isDisabled?: boolean }>`
  width: 100%;
  min-height: 280px;
  background: var(--color-surface);
  border: 2px solid ${props => props.$isCurrent ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 12px;
  padding: 24px 16px 16px 16px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  opacity: ${props => props.$isDisabled ? 0.5 : 1};

  &:hover {
    ${props => !props.$isDisabled && `
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
      border-color: var(--color-accent-strong);
    `}
  }

  ${props => props.$isCurrent && `
    background: var(--color-panel);
  `}

  ${props => props.$isDisabled && `
    filter: grayscale(0.3);
  `}
`;

const PlanTypeName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 4px;
  text-align: center;
  flex-shrink: 0;
`;

const PlanSubtitle = styled.div`
  font-size: 11px;
  color: #999;
  text-align: center;
  margin-top: 0;
  margin-bottom: 12px;
  flex-shrink: 0;
  min-height: 2.6em;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1.3;
  padding: 0 4px;
  
  /* 다크 모드: 더 밝은 회색 */
  :root:not([data-theme='light']) & {
    color: #888;
  }
`;

const PlanPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-bottom: 12px;
  flex-shrink: 0;
`;

const PlanPrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: center;

  /* 다크 모드: 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    color: white;
  }
`;

const PlanPriceOriginalRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-align: center;
`;

const PlanPriceStrikethrough = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-secondary);
  text-decoration: line-through;
  opacity: 0.6;

  /* 다크 모드 */
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const PlanPriceDivider = styled.div`
  width: 100%;
  max-width: 80px;
  height: 0;
  border-top: 1px solid var(--color-border);
  margin: 2px 0;
  :root:not([data-theme='light']) & {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const PlanPriceLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-right: 2px;
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.7);
  }
`;

const PlanBadge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-accent-strong);
  background: var(--color-background);
  border: 1px solid var(--color-accent-strong);
  margin-top: 4px;
  :root:not([data-theme='light']) & {
    color: white;
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

/** 무료 플랜: 유료 플랜의 「이용권 구입」 배지 자리 높이 맞춤 */
const PlanBadgeSpacer = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
  visibility: hidden;
  pointer-events: none;
  box-sizing: border-box;
  line-height: 1.2;
  &::after {
    content: '이용권 구입';
  }
`;

const PlanUnavailableBadge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-secondary);
  background: var(--color-background);
  border: 1px solid var(--color-border);
  margin-top: 4px;
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.5);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const PlanUpgradePrice = styled.div<{ $isDisabled?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$isDisabled ? 'var(--color-text-secondary)' : 'var(--color-accent-strong)'};
  text-align: center;

  /* 다크 모드 */
  :root:not([data-theme='light']) & {
    color: ${props => props.$isDisabled ? 'rgba(255, 255, 255, 0.5)' : 'white'};
  }
`;

const PlanDetails = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  padding-top: 12px;
`;

const FeatureCategoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-bottom: 4px;
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
    padding-bottom: 8px;
    margin-bottom: 8px;
  }
`;

const CategoryLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: left;
  margin-bottom: 6px;
  padding-left: 4px;
  min-height: 14px;
  
  /* 다크 모드: 흰색으로 표시 */
  :root:not([data-theme='light']) & {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const PlanDetailItem = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary);
  text-align: left;
  line-height: 1.4;
  word-break: keep-all;
  white-space: pre-line;
  padding-left: 8px;
  min-height: 1.4em;
  display: block;
  
  &:not(:last-child) {
    margin-bottom: 4px;
  }
`;

const CurrentRibbon = styled.div<{ $suspended?: boolean }>`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.$suspended ? '#6c757d' : 'var(--color-accent-strong)'};
  color: white;
  padding: 6px 20px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const ComparisonSection = styled.div`
  margin-top: 48px;
  margin-bottom: 300px;
`;

const ComparisonTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 24px 0;
  text-align: center;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--color-surface);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const TableHead = styled.thead`
  background: var(--color-accent-strong);
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid var(--color-border);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: white;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  
  &:last-child {
    border-right: none;
  }
  
  &:first-child {
    text-align: center;
  }
`;

const TableData = styled.td<{ $isFeature?: boolean }>`
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: var(--color-text-secondary);
  border-right: 1px solid var(--color-border);
  vertical-align: middle;
  
  &:last-child {
    border-right: none;
  }
  
  ${props => props.$isFeature && `
    font-weight: 600;
    color: var(--color-text-primary);
    text-align: left;
    background: var(--color-panel);
    white-space: pre-line;
    line-height: 1.5;
    
    /* 일반 모드: 흰색 배경 */
    :root[data-theme='light'] & {
      background: #ffffff;
    }
    
    /* 다크 모드: 배경보다 조금 밝은색 */
    :root:not([data-theme='light']) & {
      background: var(--color-surface);
    }
  `}
`;

const CategoryCell = styled.td<{ $isLastCategory?: boolean }>`
  padding: 16px;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text-primary);
  background: var(--color-panel);
  border-right: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  vertical-align: middle;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  width: 60px;
  
  /* 일반 모드: 흰색 배경 */
  :root[data-theme='light'] & {
    background: #ffffff;
  }
  
  /* 다크 모드: 배경보다 조금 밝은색 */
  :root:not([data-theme='light']) & {
    background: var(--color-surface);
  }
  
  /* 마지막 카테고리(보드)의 아래쪽 테두리 제거 */
  ${props => props.$isLastCategory && `
    border-bottom: none;
  `}
`;

const FeatureStatusBadge = styled.div<{ $isAvailable: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $isAvailable }) => ($isAvailable ? '#10504a' : '#ff6b6b')};
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
  overflow: visible;
  padding: 2px;
  
  svg {
    width: 14px;
    height: 14px;
    color: white;
    display: block;
    overflow: visible;
  }
`;

const CompanyInfoSection = styled.div`
  background: var(--color-panel);
  padding: 16px 20px 100px 20px;
  margin-top: auto;
  width: 100%;
  box-sizing: border-box;
  
  /* 다크모드에서 배경색 조정 */
  :root:not([data-theme='light']) & {
    background: rgba(0, 0, 0, 0.2);
  }
  
  /* 라이트모드에서 배경색: #10504A보다 조금 어두운 색상 */
  :root[data-theme='light'] & {
    background: #0d453f;
  }
`;

const CompanyInfoContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
`;

const CompanyLogo = styled.div`
  flex-shrink: 0;
  
  img {
    height: 20px;
    width: auto;
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    img {
      height: 18px;
    }
  }
`;

const CompanyInfoText = styled.div`
  font-size: 11px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  opacity: 0.7;
  text-align: center;
  width: 100%;
  
  /* 라이트 모드에서 텍스트를 흰색으로 */
  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
  }
  
  div {
    margin-bottom: 2px;
  }
  
  div:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const PolicyLinks = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 6px 0;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin: 4px 0;
  }
`;

const PolicyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--color-text-secondary);
  text-decoration: underline;
  opacity: 0.7;
  font-size: 11px;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
    color: var(--color-accent-strong);
  }
  
  /* 라이트 모드에서 텍스트를 흰색으로 */
  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
    
    &:hover {
      color: rgba(255, 255, 255, 1);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const CouponModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
  padding: 20px;
`;

const CouponModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  display: flex;
  flex-direction: column;
`;

const CouponModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
`;

const CouponModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: var(--color-text-gray);
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-text-primary);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CouponModalBody = styled.div`
  padding: 24px;
  flex: 1;
`;

const CouponInputLabel = styled.label`
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const CouponInputWrapper = styled.div`
  margin-bottom: 20px;
`;

const CouponInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 8px;
  width: 100%;
`;

const CouponInputMessage = styled.div`
  text-align: center;
  font-size: 13px;
  color: #ff6b6b;
  margin-top: 4px;
  padding: 0 16px;
`;

const CouponInputPart = styled.input<{ $isPrefix?: boolean }>`
  width: ${props => props.$isPrefix ? '100px' : '180px'};
  padding: 12px 16px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 2px;
  color: var(--color-text-primary);
  background: var(--color-surface);
  box-sizing: border-box;
  transition: border-color 0.2s ease;
  text-align: center;

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #b0b0b0;
    text-transform: none;
    opacity: 0.5;
  }
`;

const CouponHyphen = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
  user-select: none;
  flex-shrink: 0;
`;

const CouponButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const CouponCancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CouponSubmitButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.8);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TestPasswordModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10003;
  padding: 20px;
`;

const TestPasswordModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-border);
  display: flex;
  flex-direction: column;
`;

const TestPasswordModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
`;

const TestPasswordModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
`;

const TestPasswordModalBody = styled.div`
  padding: 24px;
  flex: 1;
`;

const TestPasswordLabel = styled.label`
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;

const TestPasswordInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  color: var(--color-text-primary);
  background: var(--color-surface);
  box-sizing: border-box;
  transition: border-color 0.2s ease;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }

  &::placeholder {
    color: #b0b0b0;
  }
`;

const TestPasswordButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const TestPasswordCancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const TestPasswordConfirmButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(var(--color-accent-rgb), 0.8);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const RefundOrderModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RefundOrderModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
`;

const RefundOrderModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const RefundOrderModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--color-text-primary);
`;

const RefundOrderModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RefundOrderMessage = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: var(--color-text-primary);
  text-align: center;

  strong {
    font-weight: 600;
    color: var(--color-accent-strong);
  }
`;

const RefundOrderButtonGroup = styled.div`
  display: flex;
  justify-content: center;
`;

const RefundOrderConfirmButton = styled.button`
  padding: 12px 24px;
  border: none;
  background: var(--color-accent-strong);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const RefundModalCancelButton = styled.button`
  padding: 12px 24px;
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--color-text-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

/* 1년 기간 확장 모달 */
const YearExtensionModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
  padding: 20px;
`;
const YearExtensionModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-xlarge);
  border: var(--modal-shell-border);
  display: flex;
  flex-direction: column;
`;
const YearExtensionModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;
const YearExtensionModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
`;
const YearExtensionModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const YearExtensionSectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
`;
const YearExtensionFixedMonths = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-secondary);
`;
const YearExtensionPaymentLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  margin-top: 8px;
`;
const YearExtensionRestrictMessage = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-primary);
  padding: 8px 0;
`;
const YearExtensionNotice = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
  strong { color: var(--color-text-primary); font-weight: 600; }
`;
const YearExtensionRowLabel = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-top: 4px;
`;
const YearExtensionOriginalPrice = styled.div`
  font-size: 18px;
  color: var(--color-text-secondary);
  text-decoration: line-through;
`;
const YearExtensionDiscountPrice = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary);
`;
const YearExtensionDiscountText = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
`;
const YearExtensionFinalLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-top: 12px;
`;
const YearExtensionFinalAmount = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--color-accent-strong);
  :root:not([data-theme='light']) & { color: white; }
`;
const LicenseAgreementBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
`;
const LicenseAgreementLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  cursor: pointer;
  user-select: none;
  text-align: left;
  & input[type='checkbox'] {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-top: 2px;
    accent-color: var(--color-accent-strong);
  }
  & span {
    flex: 1;
    text-align: left;
  }
`;
const YearExtensionModalFooter = styled.div`
  display: flex;
  border-top: 1px solid var(--color-border);
`;
const YearExtensionCancelButton = styled.button`
  flex: 1;
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-right: 1px solid var(--color-border);
  border-radius: 0 0 0 12px;
  background: var(--color-background);
  color: var(--color-text-primary);
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;
const YearExtensionSubmitButton = styled.button<{ $disabled?: boolean }>`
  flex: 1;
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 0 0 12px 0;
  background: var(--color-accent-strong);
  color: white;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.6 : 1};
  &:hover:not(:disabled) { opacity: 0.95; }
`;

/* 1년 업그레이드 모달 */
const YearUpgradeModalOverlay = styled(YearExtensionModalOverlay)``;
const YearUpgradeModalContent = styled(YearExtensionModalContent)``;
const YearUpgradeModalHeader = styled(YearExtensionModalHeader)``;
const YearUpgradeModalTitle = styled(YearExtensionModalTitle)``;
const YearUpgradeModalBody = styled(YearExtensionModalBody)``;
const YearUpgradeCostLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
`;
const YearUpgradeCostAmount = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: var(--color-accent-strong);
  text-align: center;
  margin: 8px 0 4px;
  :root:not([data-theme='light']) & { color: white; }
`;
const YearUpgradeCalculationBlock = styled.div`
  margin-top: 8px;
  padding: 10px 12px;
  background: var(--color-background);
  border-radius: 8px;
  border: 1px solid var(--color-border);
`;
const YearUpgradeCalculationLine = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  &:not(:last-child) { margin-bottom: 4px; }
`;
const YearUpgradeModalFooter = styled(YearExtensionModalFooter)``;
const YearUpgradeCancelButton = styled(YearExtensionCancelButton)``;
const YearUpgradeSubmitButton = styled(YearExtensionSubmitButton)``;

const EmptyLicenseBlock = styled.div`
  background: var(--color-panel);
  border: 3px dashed var(--color-border);
  border-radius: 8px;
  padding: 20px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 4px 0;
`;

const EmptyLicenseText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-secondary);
  text-align: center;
`;

void Divider;
void LicensePeriodText;
void CurrentPlanPeriodWrap;
void InfoValue;
void LicenseInfoMessage;
void LicenseRefundPeriodText;
void LicenseRefundAmountText;
void RefundSection;
void RefundEligibleText;
void RefundIneligibleText;
void YearExtensionPaymentLabel;
void YearExtensionOriginalPrice;
